import React, { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// =============================================================
// CONFIG
// =============================================================
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const COLORS = {
  primary: '#6B2D8B',
  accent: '#E91E8C',
  gold: '#F5C842',
  primarySoft: '#F5EEF9',
  text: '#2D2438',
};

const TOTAL_STEPS = 7;

// Logo public asset path
const LOGO_PATH = '/logo-iltuoamaro.png';

// =============================================================
// DATA
// =============================================================
const SECTORS = [
  { id: 'ristorante', icon: '🍽️', label: 'Ristorante / Pizzeria / Trattoria' },
  { id: 'bar', icon: '🍸', label: 'Bar / Lounge / Cocktail Bar' },
  { id: 'hotel', icon: '🏨', label: 'Hotel / Resort / Villaggio Turistico' },
  { id: 'gourmet', icon: '🛒', label: 'Negozio Gourmet / Bottega' },
  { id: 'agricola', icon: '🌾', label: 'Azienda Agricola' },
  { id: 'distributore', icon: '📦', label: 'Distributore / Grossista' },
  { id: 'regali', icon: '🎁', label: 'Regali Aziendali / Eventi' },
  { id: 'ecommerce', icon: '💻', label: 'E-commerce / Brand Digitale' },
  { id: 'wedding', icon: '👰', label: 'Wedding Planner / Cerimonie' },
  { id: 'altro', icon: '❓', label: 'Altro' },
];

const DESTINATIONS = [
  { id: 'rivendita', icon: '🏪', title: 'Rivendita',
    desc: 'Vendo il prodotto ai miei clienti finali — scaffale, shop online o punto vendita.',
    micro: 'Perfetto — ti proporremo formati e etichette pensati per lo scaffale.' },
  { id: 'horeca', icon: '🍸', title: 'HoReCa',
    desc: 'Servizio diretto in Hotel, Ristoranti e Caffè — al tavolo, al bancone o in sala.',
    micro: 'Ottimo — valutiamo insieme formato e identità visiva per il servizio.' },
  { id: 'sfuso', icon: '🏺', title: 'Sfuso / Bag-in-Box',
    desc: 'Distribuisco o servo il prodotto sfuso, in contenitori ricaricabili o bag-in-box.',
    micro: 'Bag-in-box o formati grandi — abbiamo le soluzioni giuste.' },
  { id: 'regali', icon: '🎁', title: 'Regali Aziendali',
    desc: 'Il prodotto è destinato a omaggi, kit regalo o eventi corporate.',
    micro: 'Packaging premium e personalizzazione totale — è il nostro forte.' },
  { id: 'eventi', icon: '🎊', title: 'Eventi e Cerimonie',
    desc: 'Matrimoni, feste private, eventi con prodotto personalizzato.',
    micro: 'Bottiglie personalizzate che raccontano il tuo momento speciale.' },
  { id: 'altro', icon: '❓', title: 'Altro', desc: 'Descrivi brevemente l\'utilizzo previsto.', micro: '' },
];

const CATEGORIES_FALLBACK = [
  { id: 'amaro', icon: '🍂', name: 'Amaro', desc: 'Il nostro punto di forza — 5 ricette esclusive, premiate a livello internazionale.', micro: 'Perfetto — abbiamo 5 ricette collaudate per te. 🏆' },
  { id: 'limoncello', icon: '🍋', name: 'Limoncello', desc: 'Fresco, elegante, sempre richiesto.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'liquirizia', icon: '🖤', name: 'Liquirizia', desc: 'Intensa e avvolgente, vera espressione calabrese.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'finocchietto', icon: '🌿', name: 'Finocchietto', desc: 'Aromatico e delicato, perfetto come digestivo.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'alloro', icon: '🌑', name: 'Alloro / Mirto', desc: 'Botaniche selvatiche del Sud Italia.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'nocino', icon: '🌰', name: 'Nocino', desc: 'Tradizione e carattere in ogni sorso.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'caffe', icon: '☕', name: 'Liquore al Caffè', desc: 'Intenso, rotondo, sempre apprezzato.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'bitter', icon: '🍊', name: 'Bitter', desc: 'Versatile, mixology-ready.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'gin', icon: '🌲', name: 'Gin', desc: 'Pluripremiato, con botaniche calabresi.', micro: 'Il nostro Gin ha vinto riconoscimenti internazionali. 🌿' },
  { id: 'grappa-barricata', icon: '🥃', name: 'Grappa Barricata', desc: 'Affinata in legno, complessità e struttura.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'grappa-bianca', icon: '🥛', name: 'Grappa Bianca', desc: 'Pulita, netta, artigianale.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'vodka', icon: '🔵', name: 'Vodka', desc: 'Neutra e versatile.', micro: 'Ottima scelta — vediamo le opzioni disponibili.' },
  { id: 'altro', icon: '🎨', name: 'Liquore Personalizzato / Altro', desc: 'Non trovi quello che cerchi? Lo creiamo da zero con il nostro mastro liquorista.', micro: 'Bene — ci raccontiamo cosa hai in mente. ✨' },
];

const AMARO_RECIPES_FALLBACK = [
  {
    id: 'amaro-agrumato', icon: '🍊', name: 'Amaro Agrumato',
    tags: ['Agrumato', 'Fresco', 'Leggero'],
    desc: 'Dominato da agrumi calabresi — bergamotto, limone e arancia. Finale fresco e dissetante.',
    tasting: 'Colore ambrato chiaro. Attacco agrumato vivace con note di bergamotto DOP. Retrogusto pulito e persistente. Ideale come aperitivo o digestivo estivo.',
  },
  {
    id: 'amaro-erbe', icon: '🌿', name: 'Amaro alle Erbe',
    tags: ['Erbaceo', 'Equilibrato', 'Classico'],
    desc: 'Un blend armonico di erbe officinali e radici. Il più versatile della linea.',
    tasting: 'Colore ambrato medio. Bouquet erbaceo complesso con note di genziana, angelica e liquirizia. Finale lungo e leggermente amaro. Il nostro best seller B2B.',
  },
  {
    id: 'amaro-amarissimo', icon: '🖤', name: 'Amaro Amarissimo',
    tags: ['Amaro intenso', 'Secco', 'Strutturato'],
    desc: 'Per i palati che cercano carattere deciso. Radici amare in primo piano, lungo finale persistente.',
    tasting: 'Colore bruno scuro. Attacco potente di china e rabarbaro. Corpo pieno, quasi tannico. Per chi non scende a compromessi.',
  },
  {
    id: 'amaro-speziato', icon: '🌶️', name: 'Amaro Speziato',
    tags: ['Speziato', 'Avvolgente'],
    desc: 'Calore di spezie mediterranee e finale lungo. Cardamomo, cannella e zenzero in evidenza.',
    tasting: 'Colore ambrato intenso. Profilo caldo con note speziate e leggera dolcezza. Perfetto dopo pasti importanti.',
  },
  {
    id: 'amaro-bergamotto', icon: '🌟', name: 'Amaro al Bergamotto DOP',
    tags: ['Bergamotto', 'Calabria', 'Iconico'],
    desc: 'L\'oro della Calabria in bottiglia. Bergamotto di Reggio DOP in primo piano, supportato da botaniche locali.',
    tasting: 'Colore dorato. Profumi intensi di bergamotto fresco, sottobosco e agrumi. Finale persistente e raffinato.',
  },
];

const GIN_RECIPES_FALLBACK = [
  {
    id: 'gin-london-dry', icon: '🌲', name: 'Gin London Dry',
    tags: ['Classico', 'Ginepro', 'Secco'],
    desc: 'Il classico London Dry — ginepro in primo piano, finale secco e pulito. Gradazione: 43% vol.',
    tasting: 'Colore cristallino. Attacco intenso di ginepro con note di coriandolo e agrumi. Finale lungo, secco e bilanciato. Perfetto per cocktail classici.',
  },
  {
    id: 'gin-agrumato', icon: '🍋', name: 'Gin Agrumato',
    tags: ['Agrumi', 'Calabria', 'Fresco'],
    desc: 'Gin agrumato calabrese — bergamotto DOP, limone, arancia e botaniche mediterranee. Gradazione: 41% vol.',
    tasting: 'Colore cristallino con riflessi dorati. Bouquet di agrumi freschi e note floreali. Finale morbido e profumato. Ideale liscio o in cocktail estivi.',
  },
];

const BOTANICALS = {
  '🍋 AGRUMI': ['Limone', 'Arancia', 'Bergamotto', 'Pompelmo', 'Pompelmo Giallo', 'Mandarino'],
  '🌱 ERBE & RADICI': ['Genziana', 'China', 'Rabarbaro', 'Angelica', 'Iris', 'Liquirizia', 'Mirto'],
  '🌳 BACCHE E SEMI': ['Ginepro', 'Coriandolo', 'Anice', 'Anice Stellato', 'Cardamomo'],
  '🌿 PIANTE': ['Rosmarino', 'Salvia', 'Finocchietto', 'Foglie di Ulivo', 'Lavanda', 'Fiori di Sambuco'],
  '🥜 FRUTTA SECCA': ['Mandorle', 'Mallo di Noce', 'Carruba'],
  '🌶️ SPEZIE': ['Cannella', 'Noce Moscata', 'Chiodi di Garofano', 'Pepe Nero', 'Peperoncino', 'Scaglie di Quercia', 'Zenzero', 'Zafferano'],
};

const INTENSITY_LABELS = ['Appena percettibile', 'Delicato', 'Bilanciato', 'Deciso', 'Dominante'];

const BOTTLE_FORMATS = ['10 cl', '50 cl', '70 cl'];

const ALCOHOL_RANGES = [
  { id: 'leggero', icon: '🍃', title: 'Leggero', range: '20%–25% vol', note: 'Ideale per liquori da dessert, digestivi delicati, limoncelli' },
  { id: 'medio', icon: '⚖️', title: 'Medio', range: '28%–35% vol', note: 'Il range più versatile — perfetto per amari e liquori da sipping' },
  { id: 'forte', icon: '🔥', title: 'Forte', range: '38%–45% vol', note: 'Carattere deciso — ideale per gin, bitter e digestivi strutturati' },
];

// =============================================================
// HELPERS
// =============================================================
// Configurazione runtime: viene popolata dalla API /api/lead-categories al primo load.
// Cade su fallback hardcoded se l'API non risponde, garantendo retro-compatibilità.
let CATEGORIES = [...CATEGORIES_FALLBACK];
let AMARO_RECIPES = [...AMARO_RECIPES_FALLBACK];
let GIN_RECIPES = [...GIN_RECIPES_FALLBACK];
let RECIPES_BY_CATEGORY = { amaro: AMARO_RECIPES_FALLBACK, gin: GIN_RECIPES_FALLBACK };
let DEFAULT_ALCOHOL = {
  amaro: 28,
  gin: 40,
  'grappa-barricata': 40,
  'grappa-bianca': 40,
};
// Categorie che NON permettono la ricetta personalizzata (solo ricetta collaudata)
let CATEGORIES_NO_CUSTOM = ['limoncello', 'liquirizia', 'finocchietto', 'alloro', 'caffe'];
// Mapping categoryId → product.id (listino) per matching esplicito quando la categoria
// è stata collegata a un prodotto esistente tramite linked_product_id (admin UI).
let CATEGORY_PRODUCT_LINKS = {};

const getDefaultAlcohol = (catId) => DEFAULT_ALCOHOL[catId] != null ? DEFAULT_ALCOHOL[catId] : 25;

// Aggiorna le strutture runtime a partire dalla risposta API /api/lead-categories.
// Ritorna true se almeno una categoria è stata caricata.
const applyLeadConfig = (data) => {
  if (!data || !Array.isArray(data.categories) || data.categories.length === 0) return false;
  const cats = data.categories.map((c) => ({
    id: c.id,
    name: c.label,
    icon: c.icon || '🍶',
    desc: c.description || '',
    micro: c.micro_copy || '',
  }));
  // Aggiungi la card "Altro" hardcoded (per liquori personalizzati su misura)
  cats.push({
    id: 'altro',
    icon: '🎨',
    name: 'Liquore Personalizzato / Altro',
    desc: "Non trovi quello che cerchi? Lo creiamo da zero con il nostro mastro liquorista.",
    micro: 'Bene — ci raccontiamo cosa hai in mente. ✨',
  });
  CATEGORIES = cats;

  const noCustom = data.categories.filter((c) => !c.allow_custom).map((c) => c.id);
  CATEGORIES_NO_CUSTOM = noCustom;

  // Costruisci la mappa di link espliciti category.id → product.id
  const links = {};
  data.categories.forEach((c) => {
    if (c.linked_product_id) links[c.id] = c.linked_product_id;
  });
  CATEGORY_PRODUCT_LINKS = links;

  const alcMap = {};
  data.categories.forEach((c) => { alcMap[c.id] = c.default_alcohol; });
  DEFAULT_ALCOHOL = alcMap;

  // Ricette raggruppate per categoria
  const byCat = {};
  (data.recipes || []).forEach((r) => {
    const rec = {
      id: r.id,
      icon: r.icon || '🍶',
      name: r.name,
      tags: r.tags || [],
      desc: r.description || '',
      tasting: r.tasting_notes || '',
    };
    if (!byCat[r.category_id]) byCat[r.category_id] = [];
    byCat[r.category_id].push(rec);
  });
  RECIPES_BY_CATEGORY = byCat;
  AMARO_RECIPES = byCat.amaro || [];
  GIN_RECIPES = byCat.gin || [];
  return true;
};

const getProductAlcohol = (p) => {
  // Se custom con valore preciso, usalo
  if (p.recipeMode === 'custom') {
    const precise = parseFloat(p.gradazione?.precise);
    if (!isNaN(precise) && precise > 0) return precise;
  }
  // Altrimenti usa il default per categoria
  return getDefaultAlcohol(p.categoryId);
};

const initialProduct = (categoryId) => ({
  categoryId,
  customName: '',           // per categoria 'altro'
  recipeMode: '',           // 'standard' | 'custom'
  ricettaScelta: '',        // id ricetta collaudata
  botaniche: [],            // [{name, category, intensity}]
  gradazione: { range: '', precise: '' },
  noteRicetta: '',
  formatItems: [],          // [{format: '50 cl', quantity: 100}] — Step 5
});

const initialState = () => ({
  // Step 1
  tipoCliente: '', // 'azienda' | 'privato'
  ragioneSociale: '',
  nome: '',
  cognome: '',
  email: '',
  telefono: '',
  // Step 2
  settori: [],
  settoreAltro: '',
  // Step 3
  destinazione: '',
  destinazioneAltro: '',
  // Step 4 — multi prodotti
  prodotti: [],   // array di product objects, uno per categoria selezionata
  // Step 5 — multi formati
  formati: [],
  fascia: '',
  quantita: '',
  // Step 6
  logoMode: '',
  logoFileName: '',
  logoFileData: '', // data URL base64 del logo (es. "data:image/png;base64,...")
  logoFileMime: '',
  brandingService: false,
  noteGrafico: '',
  // Step 7
  noteFinali: '',
});

const findLabel = (arr, id, key = 'label') => arr.find((x) => x.id === id)?.[key] || '';

const getRecipesForCategory = (catId) => RECIPES_BY_CATEGORY[catId] || [];

// SVG bottiglia vuota inline (Step 5 formati)
const EmptyBottleIcon = ({ className = '', color = COLORS.primary }) => (
  <svg viewBox="0 0 24 32" className={className} style={{ width: 22, height: 30 }} aria-hidden="true">
    <path
      d="M9 2.5h6v3.5c0 .8.2 1.5.6 2.2l1.6 2.6c.5.9.8 1.9.8 2.9V27a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 6 27V13.7c0-1 .3-2 .8-2.9l1.6-2.6c.4-.7.6-1.4.6-2.2V2.5Z"
      fill="none"
      stroke={color}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M9 2.5h6"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </svg>
);

// Load image as data URL + native dimensions (for jsPDF embedding preserving aspect ratio)
const loadImageAsDataURL = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    } catch (e) {
      resolve(null);
    }
  };
  img.onerror = () => resolve(null);
  img.src = src;
});

// =============================================================
// MAIN COMPONENT
// =============================================================
export default function LeadQualificationPage() {
  const [step, setStep] = useState(1);
  const [state, setState] = useState(initialState());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');
  const [quoteRef, setQuoteRef] = useState('');
  // Draft ID generato una volta sola — usato per upsert progressivo sul backend
  const [draftId] = useState(() => {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  });

  // Pricing data dal backend (stessi endpoint del /listino)
  const [pricingData, setPricingData] = useState({ products: [], formats: [], tiers: [] });
  // Versione config per forzare re-render quando le categorie vengono caricate dal DB
  const [configVersion, setConfigVersion] = useState(0);

  useEffect(() => {
    const loadLeadConfig = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/lead-categories`);
        if (!res.ok) return;
        const data = await res.json();
        if (applyLeadConfig(data)) {
          setConfigVersion((v) => v + 1);
        }
      } catch (e) {
        // silent — fallback hardcoded
      }
    };
    loadLeadConfig();
  }, []);

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const [pRes, fRes, tRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/products/liquor-types`).then((r) => r.ok ? r.json() : []),
          fetch(`${BACKEND_URL}/api/formats`).then((r) => r.ok ? r.json() : []),
          fetch(`${BACKEND_URL}/api/discount-tiers`).then((r) => r.ok ? r.json() : []),
        ]);
        setPricingData({
          products: Array.isArray(pRes) ? pRes : [],
          formats: Array.isArray(fRes) ? fRes : [],
          tiers: (Array.isArray(tRes) ? tRes : []).filter((t) => t.is_active !== false).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
        });
      } catch (e) {
        // silent — pricing è opzionale
      }
    };
    loadPricing();
  }, []);

  useEffect(() => {
    const id = 'gf-iltuoamaro-preventivo';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@500;600;700;800&display=swap';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Richiedi il tuo preventivo — IlTuoAmaro.it';
    return () => { document.title = prev; };
  }, []);

  // Scroll to top on step change (UX critical on mobile)
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [step, done]);

  // Salvataggio progressivo nel DB ad ogni cambio step (fire-and-forget, non blocca UI)
  // Permette all'admin di vedere dove l'utente si è bloccato
  useEffect(() => {
    if (done) return;
    // Salva solo se l'utente ha inserito almeno l'email (altrimenti tracking inutile)
    if (!state.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) return;
    try {
      // Escludi il logoFileData dal progress save: il blob base64 può eccedere il
      // limite di 64KB di keepalive e bloccare la richiesta in background.
      // Verrà comunque salvato al submit finale.
      // eslint-disable-next-line no-unused-vars
      const { logoFileData, ...stateWithoutLogoData } = state;
      fetch(`${BACKEND_URL}/api/landing-leads/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft_id: draftId,
          last_step: step,
          completed: false,
          ...stateWithoutLogoData,
        }),
        keepalive: true,
      }).catch(() => { /* silent */ });
    } catch { /* silent */ }
    // Aggiorna ad ogni cambio step (non per ogni keystroke per evitare flooding)
  }, [step]);

  const update = (patch) => setState((s) => ({ ...s, ...patch }));

  const goNext = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  // -----------------------------
  // PRODOTTI helpers (Step 4 multi)
  // -----------------------------
  const toggleCategory = (catId) => {
    setState((s) => {
      const exists = s.prodotti.find((p) => p.categoryId === catId);
      if (exists) {
        return { ...s, prodotti: s.prodotti.filter((p) => p.categoryId !== catId) };
      }
      return { ...s, prodotti: [...s.prodotti, initialProduct(catId)] };
    });
  };

  const updateProduct = (catId, patch) => {
    setState((s) => ({
      ...s,
      prodotti: s.prodotti.map((p) => (p.categoryId === catId ? { ...p, ...patch } : p)),
    }));
  };

  const toggleProductBotanica = (catId, name, category) => {
    setState((s) => ({
      ...s,
      prodotti: s.prodotti.map((p) => {
        if (p.categoryId !== catId) return p;
        const exists = p.botaniche.find((b) => b.name === name);
        if (exists) return { ...p, botaniche: p.botaniche.filter((b) => b.name !== name) };
        return { ...p, botaniche: [...p.botaniche, { name, category, intensity: 3 }] };
      }),
    }));
  };

  const setProductIntensity = (catId, name, intensity) => {
    setState((s) => ({
      ...s,
      prodotti: s.prodotti.map((p) => {
        if (p.categoryId !== catId) return p;
        return { ...p, botaniche: p.botaniche.map((b) => (b.name === name ? { ...b, intensity } : b)) };
      }),
    }));
  };

  const hasCustom = useMemo(() => state.prodotti.some((p) => p.recipeMode === 'custom'), [state.prodotti]);

  // -----------------------------
  // FORMATI helpers (Step 5 — per-prodotto)
  // -----------------------------
  const toggleProductFormat = (catId, format) => {
    setState((s) => ({
      ...s,
      prodotti: s.prodotti.map((p) => {
        if (p.categoryId !== catId) return p;
        const exists = p.formatItems.find((fi) => fi.format === format);
        if (exists) {
          return { ...p, formatItems: p.formatItems.filter((fi) => fi.format !== format) };
        }
        return { ...p, formatItems: [...p.formatItems, { format, quantity: 24 }] };
      }),
    }));
  };

  const setProductFormatQty = (catId, format, qty) => {
    const q = Math.max(0, parseInt(qty || 0, 10) || 0);
    setState((s) => ({
      ...s,
      prodotti: s.prodotti.map((p) => {
        if (p.categoryId !== catId) return p;
        return {
          ...p,
          formatItems: p.formatItems.map((fi) => (fi.format === format ? { ...fi, quantity: q } : fi)),
        };
      }),
    }));
  };

  // Aggregati derivati (per compatibilità con state.formati/state.quantita per pricing & PDF)
  const aggregatedFormati = useMemo(() => {
    const set = new Set();
    state.prodotti.forEach((p) => (p.formatItems || []).forEach((fi) => fi.format && set.add(fi.format)));
    return Array.from(set);
  }, [state.prodotti]);

  const totalQuantity = useMemo(() => {
    return state.prodotti.reduce(
      (sum, p) => sum + (p.formatItems || []).reduce((s2, fi) => s2 + (parseInt(fi.quantity, 10) || 0), 0),
      0
    );
  }, [state.prodotti]);

  // Auto-popola state.formati/state.quantita ad ogni cambiamento per coerenza con backend/PDF/pricing
  useEffect(() => {
    setState((s) => ({
      ...s,
      formati: aggregatedFormati,
      quantita: totalQuantity > 0 ? String(totalQuantity) : '',
    }));
  }, [aggregatedFormati, totalQuantity]);

  // Fascia consigliata automatica in base alla quantità totale (matcha discount_tiers)
  const suggestedFascia = useMemo(() => {
    if (totalQuantity >= 401) return 'volume';
    if (totalQuantity >= 101) return 'business';
    if (totalQuantity >= 24) return 'starter';
    return '';
  }, [totalQuantity]);

  // Auto-imposta la fascia in base alla quantità totale (per coerenza con backend/PDF)
  useEffect(() => {
    if (suggestedFascia && state.fascia !== suggestedFascia) {
      setState((s) => ({ ...s, fascia: suggestedFascia }));
    }
  }, [suggestedFascia, state.fascia]);

  // -----------------------------
  // MISSING REQUIREMENTS — UX hint
  // -----------------------------
  // Lista descrittiva di cosa manca per andare avanti dallo step corrente
  const missingRequirements = useMemo(() => {
    const items = [];
    if (step === 1) {
      if (!state.tipoCliente) items.push('Indica se sei azienda o privato');
      if (state.tipoCliente === 'azienda' && !state.ragioneSociale.trim()) items.push('Inserisci la Ragione Sociale');
      if (state.tipoCliente === 'privato') {
        if (!state.nome.trim()) items.push('Inserisci il Nome');
        if (!state.cognome.trim()) items.push('Inserisci il Cognome');
      }
      if (!state.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) items.push('Inserisci una email valida');
      if (!state.telefono.trim()) items.push('Inserisci il telefono');
    }
    if (step === 2) {
      if (state.settori.length === 0) items.push('Seleziona almeno un settore');
      if (state.settori.includes('altro') && !state.settoreAltro.trim()) items.push('Specifica il settore "Altro"');
    }
    if (step === 3) {
      if (!state.destinazione) items.push('Seleziona una destinazione del prodotto');
      if (state.destinazione === 'altro' && !state.destinazioneAltro.trim()) items.push('Specifica la destinazione "Altro"');
    }
    if (step === 4) {
      if (state.prodotti.length === 0) {
        items.push('Seleziona almeno un liquore dalla griglia');
      } else {
        state.prodotti.forEach((p) => {
          const catName = CATEGORIES.find((c) => c.id === p.categoryId)?.name || p.categoryId;
          if (p.categoryId === 'altro' && !p.customName.trim()) {
            items.push(`"${catName}": dai un nome al liquore`);
          }
          if (!p.recipeMode) {
            const allowCustom = !CATEGORIES_NO_CUSTOM.includes(p.categoryId);
            items.push(allowCustom ? `"${catName}": scegli ricetta collaudata o personalizzata` : `"${catName}": conferma la ricetta collaudata`);
          }
          if (p.recipeMode === 'standard' && getRecipesForCategory(p.categoryId).length > 0 && !p.ricettaScelta) {
            items.push(`"${catName}": seleziona una ricetta collaudata`);
          }
          if (p.recipeMode === 'custom') {
            if (p.botaniche.length < 3) items.push(`"${catName}": seleziona almeno 3 botaniche (ne hai ${p.botaniche.length})`);
            if (!p.gradazione.range) items.push(`"${catName}": scegli una gradazione alcolica`);
          }
        });
      }
    }
    if (step === 5) {
      // Verifica che ogni prodotto abbia almeno 1 formato con quantità > 0
      const productsWithoutFormat = state.prodotti.filter((p) => !(p.formatItems || []).some((fi) => (fi.quantity || 0) > 0));
      if (productsWithoutFormat.length > 0) {
        productsWithoutFormat.forEach((p) => {
          const catName = CATEGORIES.find((c) => c.id === p.categoryId)?.name || p.categoryId;
          items.push(`"${catName}": seleziona almeno un formato con quantità`);
        });
      }
      if (totalQuantity < 24 && state.prodotti.length > 0) {
        items.push(`Quantità totale minima 24 bottiglie (ora: ${totalQuantity})`);
      }
      if (hasCustom && totalQuantity > 0 && totalQuantity < 120) {
        items.push(`Per ricette personalizzate la quantità minima totale è 120 bottiglie (ora: ${totalQuantity})`);
      }
    }
    if (step === 6) {
      if (!state.logoMode) items.push('Indica come vuoi gestire il logo');
    }
    return items;
  }, [step, state, hasCustom]);

  const canProceed = missingRequirements.length === 0;

  // -----------------------------
  // VALIDATION per step (mantiene compatibility con errors map)
  // -----------------------------
  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!state.tipoCliente) e.tipoCliente = true;
      if (state.tipoCliente === 'azienda' && !state.ragioneSociale.trim()) e.ragioneSociale = true;
      if (state.tipoCliente === 'privato' && !state.nome.trim()) e.nome = true;
      if (state.tipoCliente === 'privato' && !state.cognome.trim()) e.cognome = true;
      if (!state.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) e.email = true;
      if (!state.telefono.trim()) e.telefono = true;
    }
    if (step === 2) {
      if (state.settori.length === 0) e.settori = true;
      if (state.settori.includes('altro') && !state.settoreAltro.trim()) e.settoreAltro = true;
    }
    if (step === 3) {
      if (!state.destinazione) e.destinazione = true;
      if (state.destinazione === 'altro' && !state.destinazioneAltro.trim()) e.destinazioneAltro = true;
    }
    if (step === 4) {
      if (state.prodotti.length === 0) e.prodotti = true;
      // Per ogni prodotto deve essere stato scelto recipeMode
      state.prodotti.forEach((p) => {
        if (!p.recipeMode) e[`recipeMode-${p.categoryId}`] = true;
        if (p.recipeMode === 'standard' && getRecipesForCategory(p.categoryId).length > 0 && !p.ricettaScelta) {
          e[`ricetta-${p.categoryId}`] = true;
        }
        if (p.recipeMode === 'custom') {
          if (p.botaniche.length < 3) e[`botaniche-${p.categoryId}`] = true;
          if (!p.gradazione.range) e[`gradazione-${p.categoryId}`] = true;
        }
        if (p.categoryId === 'altro' && !p.customName.trim()) e[`customName-${p.categoryId}`] = true;
      });
    }
    if (step === 5) {
      const productsWithoutFormat = state.prodotti.filter((p) => !(p.formatItems || []).some((fi) => (fi.quantity || 0) > 0));
      if (productsWithoutFormat.length > 0) e.formati = true;
      if (totalQuantity < 24 && state.prodotti.length > 0) e.minQty = true;
      if (hasCustom && totalQuantity > 0 && totalQuantity < 120) e.quantitaMin = true;
    }
    if (step === 6) {
      if (!state.logoMode) e.logoMode = true;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (validateStep()) goNext(); };

  // -----------------------------
  // PRICING SNAPSHOT (per admin /admin/landing-leads)
  // -----------------------------
  // Calcola una vista riassuntiva (JSON) dell'offerta auto-calcolata dal cliente:
  // dettagli per prodotto/formato/scaglione + totale generale.
  const buildPricingSnapshot = () => {
    const dbProducts = pricingData?.products || [];
    const dbFormats = pricingData?.formats || [];
    const tiers = [...(pricingData?.tiers || [])].sort((a, b) => (a.min_quantity || 0) - (b.min_quantity || 0));
    // parseCl locale: estrae il numero di cl da label tipo "50 cl" o "50cl"
    const parseCl = (label) => {
      const m = String(label || '').match(/(\d+)/);
      return m ? parseInt(m[1], 10) : null;
    };
    const getProd = (catId) => {
      const linkedId = CATEGORY_PRODUCT_LINKS[catId];
      if (linkedId) {
        const linked = dbProducts.find((dp) => dp.id?.toLowerCase() === String(linkedId).toLowerCase());
        if (linked) return linked;
      }
      return dbProducts.find((dp) => dp.id?.toLowerCase() === catId.toLowerCase());
    };
    const getFmt = (label) => dbFormats.find((f) => f.size_cl === parseCl(label));

    const items = [];
    let total_amount = 0;
    let total_bottles = 0;

    (state.prodotti || []).forEach((p) => {
      const cat = CATEGORIES.find((c) => c.id === p.categoryId);
      const catLabel = p.categoryId === 'altro' && p.customName ? p.customName : (cat?.name || p.categoryId);
      const recipe = (RECIPES_BY_CATEGORY[p.categoryId] || []).find((r) => r.id === p.ricettaScelta);
      const dbProduct = getProd(p.categoryId);
      const canPrice = !!(dbProduct && tiers.length > 0 && p.recipeMode === 'standard' && p.categoryId !== 'altro');

      const formatLines = (p.formatItems || []).map((fi) => {
        const qty = parseInt(fi.quantity, 10) || 0;
        const tier = tiers.find((t) => qty >= (t.min_quantity || 0) && (t.max_quantity == null || qty <= t.max_quantity));
        const dbFmt = getFmt(fi.format);
        let unit_price = null;
        let line_total = null;
        let discount_pct = 0;
        let tier_label = null;
        if (canPrice && dbFmt && tier) {
          unit_price = +(dbProduct.basePrice * dbFmt.price_multiplier * (1 - (tier.discount_percentage || 0))).toFixed(2);
          line_total = +(unit_price * qty).toFixed(2);
          discount_pct = Math.round((tier.discount_percentage || 0) * 100);
          tier_label = tier.label;
          total_amount += line_total;
        }
        total_bottles += qty;
        return {
          format: fi.format,
          quantity: qty,
          unit_price,
          line_total,
          discount_pct,
          tier_label,
        };
      });

      items.push({
        category_id: p.categoryId,
        category_label: catLabel,
        recipe_id: p.ricettaScelta || null,
        recipe_label: recipe?.name || (p.recipeMode === 'custom' ? 'Ricetta personalizzata' : null),
        recipe_mode: p.recipeMode || null,
        alcohol_pct: getProductAlcohol(p),
        product_db_id: dbProduct?.id || null,
        product_db_name: dbProduct?.name || null,
        base_price: dbProduct?.basePrice ?? null,
        priceable: canPrice,
        formats: formatLines,
      });
    });

    return {
      total_amount: +total_amount.toFixed(2),
      total_bottles,
      currency: 'EUR',
      items,
      destinazione: state.destinazione || null,
      settori: state.settori || [],
      tempistica: state.tempistica || null,
      logo_mode: state.logoMode || null,
      branding_service: !!state.brandingService,
      computed_at: new Date().toISOString(),
    };
  };

  // -----------------------------
  // PDF GENERATION
  // -----------------------------
  const generatePdf = async () => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 600));

    try {
      const ref = `ITA-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
      setQuoteRef(ref);

      // Salva lead a backend tramite progress endpoint (completed=true).
      // NB: niente keepalive — la chiamata può includere il blob base64 del logo
      // che eccede facilmente il limite di 64KB di keepalive. È sicura senza
      // keepalive perché viene eseguita PRIMA che l'utente navighi via.
      try {
        await fetch(`${BACKEND_URL}/api/landing-leads/progress`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            draft_id: draftId,
            last_step: 7,
            completed: true,
            ...state,
            quoteRef: ref,
            source: 'landing-preventivo',
          }),
        }).catch(() => {});
      } catch { /* silent */ }

      // Pre-load logo for PDF (with native dimensions to preserve aspect ratio)
      const logoData = await loadImageAsDataURL(LOGO_PATH);

      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const M = 18;
      let y = 0;

      // ===== HEADER GRADIENT BAND =====
      const steps = 60;
      const stepW = pageW / steps;
      const c1 = [107, 45, 139];
      const c2 = [233, 30, 140];
      for (let i = 0; i < steps; i += 1) {
        const t = i / (steps - 1);
        const r = c1[0] + (c2[0] - c1[0]) * t;
        const g = c1[1] + (c2[1] - c1[1]) * t;
        const b = c1[2] + (c2[2] - c1[2]) * t;
        doc.setFillColor(r, g, b);
        doc.rect(i * stepW, 0, stepW + 0.5, 6, 'F');
      }

      // ===== LOGO + TAGLINE =====
      y = 12;
      if (logoData?.dataUrl) {
        try {
          // Calcola dimensioni mantenendo aspect ratio nativo del logo
          // Target: altezza max 16mm, larghezza max 60mm
          const ratio = logoData.width / logoData.height;
          const targetH = 16;
          let drawW = targetH * ratio;
          let drawH = targetH;
          if (drawW > 60) {
            drawW = 60;
            drawH = drawW / ratio;
          }
          doc.addImage(logoData.dataUrl, 'PNG', M, y, drawW, drawH);
        } catch { /* fallback wordmark below */ }
      } else {
        // Fallback wordmark se logo non caricato
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.setTextColor(107, 45, 139);
        doc.text('il tuoamaro.it', M, y + 12);
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(124, 112, 144);
      doc.text('Riepilogo della tua richiesta B2B', M, y + 24);

      // Right column: ref & date
      doc.setFontSize(9);
      doc.setTextColor(45, 36, 56);
      const today = new Date().toLocaleDateString('it-IT');
      doc.text(`Data: ${today}`, pageW - M, y + 6, { align: 'right' });
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(233, 30, 140);
      doc.text(`Rif. ${ref}`, pageW - M, y + 12, { align: 'right' });

      // Award badge
      y = 38;
      doc.setFillColor(245, 200, 66);
      doc.roundedRect(M, y, 130, 7, 1.5, 1.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(61, 26, 82);
      doc.text('Migliore Distilleria Italiana 2025 - Frankfurt International Trophy', M + 3, y + 4.8);

      // Divider
      y += 12;
      doc.setDrawColor(233, 30, 140);
      doc.setLineWidth(0.6);
      doc.line(M, y, pageW - M, y);
      y += 6;

      // ===== SECTION HEADER HELPER =====
      const sectionHeader = (label) => {
        if (y > pageH - 30) { doc.addPage(); y = 25; }
        doc.setFillColor(107, 45, 139);
        doc.rect(M, y, pageW - 2 * M, 7, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text(label, M + 3, y + 5);
        doc.setDrawColor(233, 30, 140);
        doc.setLineWidth(0.8);
        doc.line(M, y + 7, pageW - M, y + 7);
        y += 11;
      };

      const writeKv = (label, value) => {
        if (y > pageH - 20) { doc.addPage(); y = 25; }
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(45, 36, 56);
        doc.text(`${label}:`, M, y);
        doc.setFont('helvetica', 'normal');
        const split = doc.splitTextToSize(String(value || '—'), pageW - 2 * M - 35);
        doc.text(split, M + 32, y);
        y += 5 * split.length + 1;
      };

      // ===== CLIENTE =====
      sectionHeader('CLIENTE');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const clienteName = state.tipoCliente === 'azienda'
        ? state.ragioneSociale
        : `${state.nome} ${state.cognome}`.trim();
      writeKv('Cliente', clienteName);
      writeKv('Email', state.email);
      writeKv('Telefono', state.telefono);
      writeKv('Settore', state.settori.map((s) => s === 'altro' ? `Altro: ${state.settoreAltro}` : findLabel(SECTORS, s)).join(' • '));
      writeKv('Destinazione', state.destinazione === 'altro' ? `Altro: ${state.destinazioneAltro}` : findLabel(DESTINATIONS, state.destinazione, 'title'));
      y += 3;

      // ===== PRODOTTI (uno per liquore selezionato) =====
      state.prodotti.forEach((p, idx) => {
        const cat = CATEGORIES.find((c) => c.id === p.categoryId);
        const catName = cat ? cat.name : p.categoryId;
        const isCustom = p.recipeMode === 'custom';
        const recipes = getRecipesForCategory(p.categoryId);
        const recipe = recipes.find((r) => r.id === p.ricettaScelta);

        sectionHeader(`PRODOTTO ${state.prodotti.length > 1 ? `${idx + 1} / ${state.prodotti.length}` : ''} — ${catName.toUpperCase()}`);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);

        if (p.categoryId === 'altro' && p.customName) {
          writeKv('Nome liquore', p.customName);
        }
        const tipologia = isCustom
          ? 'Ricetta Personalizzata su Misura'
          : (recipe ? recipe.name : `Ricetta collaudata ${catName}`);
        writeKv('Tipologia', tipologia);
        writeKv('Gradazione alcolica', `${getProductAlcohol(p).toFixed(0)}% vol.`);
        if (isCustom) {
          if (p.gradazione.range) writeKv('Range gradazione', p.gradazione.range);
          if (p.noteRicetta) writeKv('Note ricetta', p.noteRicetta);
        }

        // Formati selezionati per questo prodotto
        if ((p.formatItems || []).length > 0) {
          const fmtSummary = p.formatItems.map((fi) => `${fi.format} × ${fi.quantity} bot.`).join(' • ');
          writeKv('Formati & quantità', fmtSummary);
          const productTotal = p.formatItems.reduce((s, fi) => s + (parseInt(fi.quantity, 10) || 0), 0);
          writeKv('Subtotale prodotto', `${productTotal} bottiglie`);
        }
        // Botaniche table (solo custom)
        if (isCustom && p.botaniche.length > 0) {
          y += 2;
          if (y > pageH - 60) { doc.addPage(); y = 25; }
          const body = p.botaniche.map((b) => {
            const dots = '●'.repeat(b.intensity) + '○'.repeat(5 - b.intensity);
            return [b.name, b.category, dots, `${INTENSITY_LABELS[b.intensity - 1]} (${b.intensity}/5)`];
          });
          autoTable(doc, {
            startY: y,
            head: [['Botanica', 'Categoria', 'Intensità', 'Profilo']],
            body,
            theme: 'grid',
            headStyles: { fillColor: [107, 45, 139], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [245, 238, 249] },
            styles: { fontSize: 9, cellPadding: 2.5 },
            margin: { left: M, right: M },
          });
          y = doc.lastAutoTable.finalY + 3;
        }
        y += 3;
      });

      // ===== TOTALI ORDINE =====
      const grandTotal = state.prodotti.reduce(
        (sum, pp) => sum + (pp.formatItems || []).reduce((s, fi) => s + (parseInt(fi.quantity, 10) || 0), 0),
        0
      );
      sectionHeader('TOTALI ORDINE');
      writeKv('Totale bottiglie', `${grandTotal} bottiglie`);
      writeKv('Fascia', state.fascia ? state.fascia.charAt(0).toUpperCase() + state.fascia.slice(1) : '—');
      y += 3;

      // ===== OFFERTA ECONOMICA INDICATIVA =====
      const { products: dbProducts = [], formats: dbFmts = [], tiers: dbTiers = [] } = pricingData || {};
      const sortedTiersPdf = [...dbTiers].sort((a, b) => (a.min_quantity || 0) - (b.min_quantity || 0));
      const parseCl = (label) => {
        const m = label?.match(/(\d+)\s*cl/i);
        return m ? parseInt(m[1], 10) : null;
      };
      const findTier = (q) => sortedTiersPdf.find((t) => q >= (t.min_quantity || 0) && (t.max_quantity == null || q <= t.max_quantity));

      // Costruisci righe offerta (solo prodotti standard con DB match)
      const offerRows = [];
      let pdfGrandTotal = 0;
      let hasUndefined = false;

      state.prodotti.forEach((p) => {
        const cat = CATEGORIES.find((c) => c.id === p.categoryId);
        const dbProd = dbProducts.find((dp) => dp.id?.toLowerCase() === p.categoryId.toLowerCase());
        const isStandardPriceable = dbProd && p.recipeMode === 'standard' && p.categoryId !== 'altro';

        (p.formatItems || []).forEach((fi) => {
          const qty = parseInt(fi.quantity, 10) || 0;
          if (qty <= 0) return;
          const dbFmt = dbFmts.find((f) => f.size_cl === parseCl(fi.format));
          const prodName = p.categoryId === 'altro' && p.customName
            ? p.customName
            : (cat?.name || p.categoryId);

          if (isStandardPriceable && dbFmt) {
            const tier = findTier(qty);
            const discount = tier?.discount_percentage || 0;
            const unit = dbProd.basePrice * dbFmt.price_multiplier * (1 - discount);
            const lineTotal = unit * qty;
            pdfGrandTotal += lineTotal;
            offerRows.push([
              prodName,
              fi.format,
              String(qty),
              tier?.label || '—',
              discount > 0 ? `-${Math.round(discount * 100)}%` : '—',
              `EUR ${unit.toFixed(2)}`,
              `EUR ${lineTotal.toFixed(2)}`,
            ]);
          } else {
            hasUndefined = true;
            offerRows.push([
              prodName,
              fi.format,
              String(qty),
              'Su misura',
              '—',
              'Da definire',
              'Da definire',
            ]);
          }
        });
      });

      if (offerRows.length > 0) {
        if (y > pageH - 80) { doc.addPage(); y = 25; }
        sectionHeader('OFFERTA ECONOMICA INDICATIVA');
        autoTable(doc, {
          startY: y,
          head: [['Prodotto', 'Formato', 'Qta', 'Fascia', 'Sconto', 'Prezzo unit.', 'Totale']],
          body: offerRows,
          theme: 'grid',
          headStyles: { fillColor: [107, 45, 139], textColor: 255, fontStyle: 'bold', fontSize: 8 },
          alternateRowStyles: { fillColor: [245, 238, 249] },
          styles: { fontSize: 8.5, cellPadding: 2.5 },
          columnStyles: {
            2: { halign: 'right' },
            5: { halign: 'right', fontStyle: 'bold' },
            6: { halign: 'right', fontStyle: 'bold', textColor: [107, 45, 139] },
          },
          margin: { left: M, right: M },
        });
        y = doc.lastAutoTable.finalY + 3;

        // Grand total box
        if (y > pageH - 30) { doc.addPage(); y = 25; }
        doc.setFillColor(107, 45, 139);
        doc.roundedRect(M, y, pageW - 2 * M, 12, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text('TOTALE ORDINE INDICATIVO (IVA esclusa)', M + 4, y + 7.5);
        doc.setFontSize(14);
        doc.text(`EUR ${pdfGrandTotal.toFixed(2)}`, pageW - M - 4, y + 7.5, { align: 'right' });
        y += 16;

        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(124, 112, 144);
        const offerNote = hasUndefined
          ? 'Prezzi indicativi su listino ufficiale. Le righe "Da definire" si riferiscono a ricette personalizzate o prodotti su misura: i prezzi saranno comunicati dal nostro team commerciale entro 24 ore lavorative.'
          : 'Prezzi indicativi su listino ufficiale. I prezzi definitivi saranno confermati dal team commerciale entro 24 ore lavorative.';
        const offerNoteSplit = doc.splitTextToSize(offerNote, pageW - 2 * M);
        doc.text(offerNoteSplit, M, y);
        y += 4 * offerNoteSplit.length + 4;
        doc.setTextColor(45, 36, 56);
        doc.setFont('helvetica', 'normal');
      }

      // ===== SERVIZI INCLUSI =====
      if (y > pageH - 60) { doc.addPage(); y = 25; }
      sectionHeader('SERVIZI INCLUSI');
      const services = [
        'Campioni di assaggio pre-produzione',
        'Progetto grafico etichetta professionale',
        'Rendering 3D bottiglia',
        'Spedizione in tutta Italia',
      ];
      if (hasCustom) services.push('Consulenza ricetta personalizzata con mastro liquorista');
      if (state.brandingService) services.push('Servizio di branding professionale');

      doc.setFontSize(10);
      services.forEach((s) => {
        doc.setTextColor(107, 45, 139);
        doc.setFont('helvetica', 'bold');
        doc.text('✓', M, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(45, 36, 56);
        doc.text(s, M + 6, y);
        y += 6;
      });
      y += 2;

      // ===== NOTE FINALI =====
      if (state.noteFinali.trim()) {
        if (y > pageH - 50) { doc.addPage(); y = 25; }
        sectionHeader('NOTE AGGIUNTIVE');
        doc.setFontSize(10);
        const nf = doc.splitTextToSize(state.noteFinali, pageW - 2 * M);
        doc.text(nf, M, y);
        y += 5 * nf.length + 5;
      }

      // ===== COSA SUCCEDE ORA (timeline 6 step) =====
      if (y > pageH - 100) { doc.addPage(); y = 25; }
      sectionHeader('CHE SUCCEDE ORA?');
      const nextStepsPdf = [
        ['1', 'Richiesta ricevuta', 'Il nostro commerciale ti contattera a breve.'],
        ['2', 'Accetta il preventivo', 'Ricevi e conferma l\'offerta personalizzata.'],
        ['3', 'Assaggia i campioni', 'Riceverai i campioni al tuo indirizzo.'],
        ['4', 'Conferma la grafica', 'Approvi l\'etichetta del tuo liquore.'],
        ['5', 'Produciamo per te', 'Il nostro mastro distillatore realizza la tua ricetta.'],
        ['6', 'Spediamo i liquori', 'Consegna direttamente alla tua sede.'],
      ];
      autoTable(doc, {
        startY: y,
        body: nextStepsPdf,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 2.5, valign: 'middle' },
        columnStyles: {
          0: { halign: 'center', valign: 'middle', cellWidth: 10, fontStyle: 'bold', textColor: [255, 255, 255], fillColor: [233, 30, 140] },
          1: { fontStyle: 'bold', textColor: [107, 45, 139], cellWidth: 45 },
          2: { textColor: [80, 70, 95] },
        },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 5;

      // Box "Sei in mano a una distilleria premiata"
      if (y > pageH - 25) { doc.addPage(); y = 25; }
      doc.setFillColor(245, 200, 66);
      doc.roundedRect(M, y, pageW - 2 * M, 10, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(61, 26, 82);
      doc.text('Sei in mano a una distilleria premiata Migliore Distilleria Italiana 2025 - Frankfurt Trophy.', pageW / 2, y + 6.5, { align: 'center' });
      y += 14;

      // ===== FOOTER ON EACH PAGE =====
      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p += 1) {
        doc.setPage(p);
        doc.setDrawColor(233, 30, 140);
        doc.setLineWidth(0.4);
        doc.line(M, pageH - 22, pageW - M, pageH - 22);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(107, 45, 139);
        doc.text('LACINIO SRL', pageW / 2, pageH - 18, { align: 'center' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(124, 112, 144);
        doc.text('Via G. Mercalli 16 Z.I.  |  88900 Crotone (KR)  |  P.IVA 03714460791  |  REA KR-184420  |  Codice Accisa IT00KRX00033F', pageW / 2, pageH - 14, { align: 'center' });
        doc.text('info@iltuoamaro.it  |  preventivi@iltuoamaro.it  |  +39 0962 937186  |  +39 393 9587340  |  iltuoamaro.it', pageW / 2, pageH - 10, { align: 'center' });

        doc.setFont('helvetica', 'italic');
        doc.setFontSize(6.5);
        doc.text('Documento di riepilogo della richiesta. L\'offerta personalizzata con prezzi definitivi verra inviata dal nostro team entro 24 ore lavorative.', pageW / 2, pageH - 6, { align: 'center' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text(`Pagina ${p} / ${totalPages}`, pageW - M, pageH - 6, { align: 'right' });
      }

      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);

      // Auto-download
      const tempLink = document.createElement('a');
      tempLink.href = url;
      tempLink.download = `Richiesta_${ref}.pdf`;
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);

      // Allega il PDF + lo snapshot prezzi al lead (admin /admin/landing-leads)
      try {
        const pdfDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read PDF blob'));
          reader.readAsDataURL(blob);
        });
        const snapshot = buildPricingSnapshot();
        await fetch(`${BACKEND_URL}/api/landing-leads/${encodeURIComponent(draftId)}/attach-quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pricing_snapshot: snapshot,
            quote_pdf_data: pdfDataUrl,
            quote_pdf_mime: 'application/pdf',
            quoteRef: ref,
          }),
        });
      } catch (e) {
        // silent: download to client comunque andato a buon fine
      }

      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  const progressPct = useMemo(() => (step / TOTAL_STEPS) * 100, [step]);

  if (done) {
    const nextSteps = [
      { icon: '📞', title: 'Richiesta ricevuta', desc: 'Il nostro commerciale ti contatterà a breve.' },
      { icon: '✅', title: 'Accetta il preventivo', desc: 'Ricevi e conferma l\'offerta personalizzata.' },
      { icon: '🥃', title: 'Assaggia i campioni', desc: 'Riceverai i campioni al tuo indirizzo.' },
      { icon: '🎨', title: 'Conferma la grafica', desc: 'Approvi l\'etichetta del tuo liquore.' },
      { icon: '⚗️', title: 'Produciamo per te', desc: 'Il nostro mastro distillatore realizza la tua ricetta.' },
      { icon: '🚚', title: 'Spediamo i liquori', desc: 'Consegna direttamente alla tua sede.' },
    ];
    return (
      <Shell>
        <div className="text-center py-10" data-testid="lead-success">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="font-display text-3xl mb-2" style={{ color: COLORS.primary }}>Abbiamo ricevuto la tua richiesta!</h2>
          <p className="text-gray-600 mb-6">
            Stiamo ultimando la tua offerta personalizzata e ti sarà recapitata al tuo indirizzo email <strong>{state.email}</strong> entro 24 ore lavorative.
          </p>
          <p className="text-sm text-gray-500 mb-6">Riferimento richiesta: <strong>{quoteRef}</strong></p>

          {pdfBlobUrl && (
            <a
              href={pdfBlobUrl}
              download={`Richiesta_${quoteRef}.pdf`}
              className="inline-flex items-center px-6 py-3 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all mb-10"
              style={{ backgroundColor: COLORS.primary }}
              data-testid="lead-download-pdf-btn"
            >
              📄 Scarica il dettaglio della tua richiesta
            </a>
          )}

          {/* Timeline prossimi step */}
          <div className="mt-8 text-left max-w-2xl mx-auto" data-testid="lead-next-steps">
            <h3 className="font-display text-2xl mb-5 text-center" style={{ color: COLORS.primary }}>
              Che succede ora?
            </h3>
            <ol className="relative space-y-4">
              {/* Linea verticale connecting */}
              <div className="absolute left-5 top-3 bottom-3 w-0.5 hidden sm:block" style={{ backgroundColor: COLORS.primarySoft }} aria-hidden="true" />
              {nextSteps.map((s, idx) => (
                <li
                  key={idx}
                  className="relative flex gap-4 items-start p-3 rounded-lg transition-all"
                  style={{ backgroundColor: idx === 0 ? '#FDF2F8' : 'transparent' }}
                  data-testid={`next-step-${idx + 1}`}
                >
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold relative z-10"
                    style={{
                      backgroundColor: idx === 0 ? COLORS.accent : 'white',
                      color: idx === 0 ? 'white' : COLORS.primary,
                      border: `2px solid ${idx === 0 ? COLORS.accent : COLORS.primarySoft}`,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xl">{s.icon}</span>
                      <h4 className="font-semibold text-sm sm:text-base" style={{ color: COLORS.text }}>{s.title}</h4>
                      {idx === 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: COLORS.accent, color: 'white' }}>
                          IN CORSO
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Box rassicurazione finale */}
          <div className="mt-10 mx-auto max-w-2xl p-4 rounded-lg" style={{ backgroundColor: COLORS.gold, color: COLORS.text }}>
            <p className="text-sm font-medium">
              🏆 Sei in mano a una distilleria <strong>Premiata Migliore Distilleria Italiana 2025</strong> al Frankfurt International Trophy.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2 text-sm font-medium" style={{ color: COLORS.primary }}>
          <span>Step {step} di {TOTAL_STEPS}</span>
          <span className="text-gray-400">{Math.round(progressPct)}%</span>
        </div>
        <div className="w-full h-2 rounded-full bg-purple-100 overflow-hidden">
          <div className="h-full transition-all duration-500" style={{ width: `${progressPct}%`, background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.accent})` }} />
        </div>
      </div>

      {/* Step content */}
      <div data-testid={`lead-step-${step}`} className="animate-fadeIn">
        {step === 1 && <Step1 state={state} update={update} errors={errors} />}
        {step === 2 && <Step2 state={state} update={update} errors={errors} />}
        {step === 3 && <Step3 state={state} update={update} errors={errors} />}
        {step === 4 && (
          <Step4
            state={state}
            toggleCategory={toggleCategory}
            updateProduct={updateProduct}
            toggleProductBotanica={toggleProductBotanica}
            setProductIntensity={setProductIntensity}
            errors={errors}
          />
        )}
        {step === 5 && (
          <Step5
            state={state}
            hasCustom={hasCustom}
            totalQuantity={totalQuantity}
            suggestedFascia={suggestedFascia}
            toggleProductFormat={toggleProductFormat}
            setProductFormatQty={setProductFormatQty}
            pricingData={pricingData}
            errors={errors}
          />
        )}
        {step === 6 && <Step6 state={state} update={update} errors={errors} />}
        {step === 7 && <Step7 state={state} update={update} submitting={submitting} onGenerate={generatePdf} goBack={goBack} pricingData={pricingData} />}
      </div>

      {/* Nav buttons */}
      {step < 7 && (
        <div className="mt-8">
          {/* Banner cosa manca per andare avanti */}
          {missingRequirements.length > 0 && (
            <div
              className="mb-4 rounded-lg border-2 border-dashed p-4 animate-fadeIn"
              style={{ borderColor: COLORS.accent, backgroundColor: '#FDF2F8' }}
              data-testid="lead-missing-banner"
            >
              <div className="flex items-start gap-3">
                <div className="text-2xl" aria-hidden="true">👇</div>
                <div className="flex-1">
                  <div className="font-semibold text-sm mb-1.5" style={{ color: COLORS.accent }}>
                    Per andare avanti ti serve ancora:
                  </div>
                  <ul className="space-y-1 text-sm" style={{ color: COLORS.text }}>
                    {missingRequirements.slice(0, 5).map((m, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-pink-500 mt-0.5">•</span>
                        <span>{m}</span>
                      </li>
                    ))}
                    {missingRequirements.length > 5 && (
                      <li className="text-xs italic text-gray-500">...e altri {missingRequirements.length - 5} elementi</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center gap-3 flex-wrap">
            {step > 1 ? (
              <button onClick={goBack} className="text-sm font-medium text-gray-600 hover:text-gray-900 px-4 py-2" data-testid="lead-back-btn">
                ← Indietro
              </button>
            ) : <span />}
            <button
              onClick={handleNext}
              data-testid="lead-next-btn"
              disabled={!canProceed}
              className={`w-full sm:w-auto px-8 py-3 rounded-lg font-semibold text-white shadow-md transition-all ${canProceed ? 'hover:shadow-lg' : 'cursor-not-allowed opacity-60'}`}
              style={{ backgroundColor: canProceed ? COLORS.primary : '#A0A0AC' }}
              title={canProceed ? '' : 'Completa i campi richiesti'}
            >
              {canProceed ? 'Avanti →' : `Manca${missingRequirements.length === 1 ? '' : 'no'} ${missingRequirements.length} ${missingRequirements.length === 1 ? 'campo' : 'campi'}`}
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
}

// ============================================================
// SHELL
// ============================================================
function Shell({ children }) {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", color: COLORS.text }} className="min-h-screen bg-white">
      <style>{`
        .font-display { font-family: 'Manrope', 'Inter', system-ui, sans-serif; font-weight: 700; letter-spacing: -0.02em; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px);} to {opacity:1; transform: none;} }
        .animate-fadeIn { animation: fadeIn .35s ease both; }
        @keyframes softPulse { 0%,100%{ box-shadow: 0 0 0 0 rgba(107,45,139,.4);} 50%{ box-shadow: 0 0 0 12px rgba(107,45,139,0);} }
        .pulse-cta { animation: softPulse 1.8s infinite; }
      `}</style>

      {/* Header */}
      <header className="border-b" style={{ borderColor: COLORS.primarySoft }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <img
            src={LOGO_PATH}
            alt="il tuoamaro.it"
            className="block h-auto"
            style={{ width: '180px', maxWidth: '60vw' }}
            data-testid="lead-header-logo"
          />
          <div className="inline-flex items-center text-xs sm:text-sm px-3 py-1.5 rounded-full font-semibold" style={{ backgroundColor: COLORS.gold, color: COLORS.text }}>
            🏆 Migliore Distilleria Italiana 2025
          </div>
        </div>
      </header>

      {/* Landing hero pre-form */}
      <section className="border-b" style={{ borderColor: COLORS.primarySoft, background: '#f7f5fb' }}>
        <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="order-2 md:order-1">
            <div className="flex items-center gap-1 text-2xl mb-2" aria-label="5 stelle su 5">
              {['★','★','★','★','★'].map((s, i) => (
                <span key={i} style={{ color: '#f59e0b' }}>{s}</span>
              ))}
            </div>
            <p className="text-base sm:text-lg font-semibold" style={{ color: COLORS.primary }}>
              Operi nel mondo dell&rsquo;HO.RE.CA?
            </p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight mt-1 mb-5" style={{ color: COLORS.text }}>
              Smetti di offrire<br/>il solito <span style={{ color: COLORS.primary }}>Amaro!</span>
            </h1>
            <p className="text-sm sm:text-base mb-4" style={{ color: COLORS.text }}>
              Creiamo per te <b>amari, gin, liquori aromatizzati e distillati</b> in <b>private label</b>: la tua ricetta, la tua etichetta, il tuo brand.
            </p>
            <ul className="space-y-2.5 text-sm sm:text-base">
              {[
                <>Creiamo il tuo <b>liquore personalizzato</b> (amaro, gin, aromatico)</>,
                <><b>Etichetta</b> personalizzata con il tuo logo</>,
                <>Fidelizza i tuoi clienti con un prodotto <b>unico</b></>,
                <><b>Ordine minimo 24 bottiglie</b></>,
                <><b>Spedizione gratuita</b> in tutta Italia</>,
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: '#10b981', color: 'white' }}>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span style={{ color: COLORS.text }}>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a
                href="#preventivo-form"
                onClick={(e) => { e.preventDefault(); const el = document.getElementById('preventivo-form'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className="pulse-cta inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-white shadow-lg hover:opacity-90 transition"
                style={{ backgroundColor: COLORS.primary }}
                data-testid="hero-cta-primary"
              >
                Richiedi il tuo preventivo →
              </a>
              <a
                href="#preventivo-form"
                onClick={(e) => { e.preventDefault(); const el = document.getElementById('preventivo-form'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold border-2 transition hover:bg-white"
                style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                data-testid="hero-cta-secondary"
              >
                Configura in 2 minuti
              </a>
            </div>
            <p className="mt-3 text-xs opacity-70" style={{ color: COLORS.text }}>
              Compila il form qui sotto: risposta entro 24h. Nessun impegno.
            </p>
          </div>
          <div className="order-1 md:order-2 flex justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_c1370480-23ca-44c6-9bba-0ab9933e4ae2/artifacts/2uyy8h9g_bottiglia-iltuoamaro.it.png"
              alt="Bottiglia liquore private label con etichetta personalizzata e il tuo logo"
              className="max-w-full h-auto rounded-lg"
              style={{ maxHeight: '520px' }}
              loading="eager"
              data-testid="hero-bottle-image"
            />
          </div>
        </div>
      </section>

      <main id="preventivo-form" className="max-w-3xl mx-auto px-4 py-8 sm:py-12 scroll-mt-16">{children}</main>
    </div>
  );
}

// ============================================================
// STEP COMPONENTS
// ============================================================
function StepHeader({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h1 className="font-display text-3xl sm:text-4xl mb-2" style={{ color: COLORS.primary, fontWeight: 700 }}>{title}</h1>
      {subtitle && <p className="text-gray-600 text-sm sm:text-base">{subtitle}</p>}
    </div>
  );
}

function inputBase(err) {
  return `w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${err ? 'border-pink-500 bg-pink-50' : 'border-gray-300 focus:border-purple-500'}`;
}

// ---- STEP 1 ----
function Step1({ state, update, errors }) {
  return (
    <>
      <StepHeader title="Iniziamo a conoscerti" subtitle="Ci vogliono meno di 3 minuti." />
      <div className="grid grid-cols-2 gap-3 mb-5">
        <ToggleBtn active={state.tipoCliente === 'azienda'} onClick={() => update({ tipoCliente: 'azienda' })} testid="cliente-azienda">
          🏢 Sono un'azienda
        </ToggleBtn>
        <ToggleBtn active={state.tipoCliente === 'privato'} onClick={() => update({ tipoCliente: 'privato' })} testid="cliente-privato">
          👤 Sono un privato
        </ToggleBtn>
      </div>

      {state.tipoCliente === 'azienda' && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1.5">Ragione Sociale *</label>
          <input
            type="text"
            value={state.ragioneSociale}
            onChange={(e) => update({ ragioneSociale: e.target.value })}
            className={inputBase(errors.ragioneSociale)}
            placeholder="Es: La Tua Azienda Srl"
            data-testid="input-ragione-sociale"
          />
        </div>
      )}
      {state.tipoCliente === 'privato' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">Nome *</label>
            <input type="text" value={state.nome} onChange={(e) => update({ nome: e.target.value })} className={inputBase(errors.nome)} data-testid="input-nome" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Cognome *</label>
            <input type="text" value={state.cognome} onChange={(e) => update({ cognome: e.target.value })} className={inputBase(errors.cognome)} data-testid="input-cognome" />
          </div>
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1.5">Email *</label>
        <input type="email" value={state.email} onChange={(e) => update({ email: e.target.value })} className={inputBase(errors.email)} placeholder="nome@email.it" data-testid="input-email" />
      </div>
      <div className="mb-2">
        <label className="block text-sm font-medium mb-1.5">Telefono *</label>
        <input type="tel" value={state.telefono} onChange={(e) => update({ telefono: e.target.value })} className={inputBase(errors.telefono)} placeholder="+39 000 0000000" data-testid="input-telefono" />
      </div>
    </>
  );
}

function ToggleBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`p-4 sm:p-5 rounded-lg border-2 font-medium transition-all text-sm sm:text-base ${active ? 'shadow-md' : 'hover:border-gray-400'}`}
      style={{
        borderColor: active ? COLORS.primary : '#E5E7EB',
        backgroundColor: active ? COLORS.primarySoft : 'white',
        color: active ? COLORS.primary : COLORS.text,
      }}
    >
      {children}
    </button>
  );
}

// ---- STEP 2 ----
function Step2({ state, update, errors }) {
  const toggle = (id) => {
    const exists = state.settori.includes(id);
    update({ settori: exists ? state.settori.filter((s) => s !== id) : [...state.settori, id] });
  };
  return (
    <>
      <StepHeader title="Che tipo di attività hai?" subtitle="Selezioniamo le soluzioni più adatte a te." />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="sectors-grid">
        {SECTORS.map((s) => (
          <SelectableCard key={s.id} selected={state.settori.includes(s.id)} onClick={() => toggle(s.id)} testid={`sector-${s.id}`}>
            <div className="text-2xl mb-1">{s.icon}</div>
            <div className="text-xs sm:text-sm font-medium leading-tight">{s.label}</div>
          </SelectableCard>
        ))}
      </div>
      {errors.settori && <p className="mt-3 text-sm" style={{ color: COLORS.accent }}>Seleziona almeno un settore.</p>}
      {state.settori.includes('altro') && (
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1.5">Specifica il tuo settore *</label>
          <input type="text" value={state.settoreAltro} onChange={(e) => update({ settoreAltro: e.target.value })} className={inputBase(errors.settoreAltro)} data-testid="input-settore-altro" />
        </div>
      )}
    </>
  );
}

function SelectableCard({ selected, onClick, children, testid, full }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-3 sm:p-4 rounded-lg border-2 transition-all w-full ${selected ? 'shadow-md' : 'hover:border-gray-400 hover:shadow-sm'} ${full ? 'col-span-full' : ''}`}
      style={{
        borderColor: selected ? COLORS.primary : '#E5E7EB',
        backgroundColor: selected ? COLORS.primarySoft : 'white',
      }}
    >
      {children}
    </button>
  );
}

// ---- STEP 3 ----
function Step3({ state, update, errors }) {
  const sel = DESTINATIONS.find((d) => d.id === state.destinazione);
  return (
    <>
      <StepHeader title="A cosa è destinato il tuo prodotto?" subtitle="Ci aiuta a capire il contesto e proporti la soluzione più adatta." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="destinations-grid">
        {DESTINATIONS.map((d) => (
          <SelectableCard key={d.id} selected={state.destinazione === d.id} onClick={() => update({ destinazione: d.id })} testid={`dest-${d.id}`}>
            <div className="flex items-start gap-3">
              <div className="text-2xl">{d.icon}</div>
              <div>
                <div className="font-semibold mb-1">{d.title}</div>
                <div className="text-xs text-gray-500 leading-snug">{d.desc}</div>
              </div>
            </div>
          </SelectableCard>
        ))}
      </div>
      {errors.destinazione && <p className="mt-3 text-sm" style={{ color: COLORS.accent }}>Seleziona una destinazione.</p>}
      {state.destinazione === 'altro' && (
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1.5">Descrivi brevemente l'utilizzo previsto *</label>
          <input type="text" value={state.destinazioneAltro} onChange={(e) => update({ destinazioneAltro: e.target.value })} className={inputBase(errors.destinazioneAltro)} data-testid="input-destinazione-altro" />
        </div>
      )}
      {sel && sel.micro && (
        <p className="mt-4 text-sm font-medium" style={{ color: COLORS.accent }}>{sel.micro}</p>
      )}
    </>
  );
}

// ---- STEP 4 ----
function Step4({ state, toggleCategory, updateProduct, toggleProductBotanica, setProductIntensity, errors }) {
  return (
    <>
      <StepHeader
        title="Quali liquori vuoi creare?"
        subtitle="Puoi selezionarne più di uno. Per ognuno scegli ricetta collaudata o personalizzata."
      />

      {/* Selezione multi-categoria */}
      <h3 className="font-display text-xl mb-3" style={{ color: COLORS.primary }}>Seleziona uno o più liquori</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        {CATEGORIES.map((c) => {
          const isSelected = state.prodotti.some((p) => p.categoryId === c.id);
          const defaultAlcohol = getDefaultAlcohol(c.id);
          return (
            <SelectableCard
              key={c.id}
              selected={isSelected}
              onClick={() => toggleCategory(c.id)}
              testid={`cat-${c.id}`}
            >
              <div className="text-3xl mb-1">{c.icon}</div>
              <div className="font-semibold text-sm">{c.name}</div>
              {c.id !== 'altro' && (
                <div
                  className="mt-1 inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold"
                  style={{ backgroundColor: COLORS.primarySoft, color: COLORS.primary }}
                  data-testid={`gradazione-badge-${c.id}`}
                >
                  {defaultAlcohol}% vol.
                </div>
              )}
              <div className="text-xs text-gray-500 mt-1 leading-snug">{c.desc}</div>
              {isSelected && (
                <div className="mt-2 inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: COLORS.accent, color: 'white' }}>
                  ✓ Selezionato
                </div>
              )}
            </SelectableCard>
          );
        })}
      </div>
      {errors.prodotti && <p className="mb-3 text-sm" style={{ color: COLORS.accent }}>Seleziona almeno un liquore.</p>}

      {/* Per ogni prodotto: pannello configurazione */}
      {state.prodotti.length > 0 && (
        <div className="space-y-5 mt-4">
          <p className="text-sm font-medium" style={{ color: COLORS.accent }}>
            Hai selezionato {state.prodotti.length} liquor{state.prodotti.length === 1 ? 'e' : 'i'} — configura ciascuno qui sotto.
          </p>
          {state.prodotti.map((p) => (
            <ProductPanel
              key={p.categoryId}
              product={p}
              updateProduct={(patch) => updateProduct(p.categoryId, patch)}
              toggleBotanica={(name, category) => toggleProductBotanica(p.categoryId, name, category)}
              setIntensity={(name, i) => setProductIntensity(p.categoryId, name, i)}
              errors={errors}
            />
          ))}
        </div>
      )}
    </>
  );
}

function ProductPanel({ product, updateProduct, toggleBotanica, setIntensity, errors }) {
  const cat = CATEGORIES.find((c) => c.id === product.categoryId);
  const recipes = getRecipesForCategory(product.categoryId);
  const catId = product.categoryId;
  const allowCustom = !CATEGORIES_NO_CUSTOM.includes(catId);

  // Cosa serve ancora per questo prodotto (per evidenziazione contestuale)
  const productMissing = [];
  if (catId === 'altro' && !product.customName.trim()) productMissing.push('Dai un nome al liquore');
  if (!product.recipeMode) productMissing.push(allowCustom ? 'Scegli ricetta collaudata o personalizzata qui sotto' : 'Conferma la ricetta collaudata qui sotto');
  if (product.recipeMode === 'standard' && recipes.length > 0 && !product.ricettaScelta) {
    productMissing.push('Seleziona una ricetta collaudata');
  }
  if (product.recipeMode === 'custom') {
    if (product.botaniche.length < 3) productMissing.push(`Aggiungi botaniche (ne servono almeno 3, ne hai ${product.botaniche.length})`);
    if (!product.gradazione.range) productMissing.push('Scegli la gradazione alcolica');
  }
  const isIncomplete = productMissing.length > 0;

  return (
    <div
      className={`rounded-xl border-2 p-4 sm:p-5 animate-fadeIn transition-all ${isIncomplete ? 'shadow-md' : ''}`}
      style={{
        borderColor: isIncomplete ? COLORS.accent : COLORS.primarySoft,
        backgroundColor: isIncomplete ? '#FDF6FB' : '#FCFBFD',
      }}
      data-testid={`product-panel-${catId}`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="text-2xl">{cat?.icon}</div>
        <h4 className="font-display text-lg" style={{ color: COLORS.primary }}>{cat?.name}</h4>
        {catId !== 'altro' && (
          <span
            className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: COLORS.primarySoft, color: COLORS.primary }}
            data-testid={`product-panel-gradazione-${catId}`}
          >
            {getProductAlcohol(product).toFixed(0)}% vol.
          </span>
        )}
        {isIncomplete ? (
          <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: COLORS.accent, color: 'white' }}>
            ⚠ Da completare
          </span>
        ) : (
          <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
            ✓ Pronto
          </span>
        )}
      </div>

      {/* Mini-hint contestuale sul pannello */}
      {isIncomplete && (
        <div className="mb-3 text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: '#FCE7F3', color: '#9D174D' }}>
          <b>Manca ancora:</b> {productMissing[0]}
        </div>
      )}

      {/* Nome custom per categoria 'altro' */}
      {catId === 'altro' && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1.5">Come si chiama il liquore che vuoi creare? *</label>
          <input
            type="text"
            value={product.customName}
            onChange={(e) => updateProduct({ customName: e.target.value })}
            className={inputBase(errors[`customName-${catId}`])}
            placeholder="Es: Elisir di Nonna, Crema di Pistacchio..."
            data-testid={`input-customname-${catId}`}
          />
        </div>
      )}

      {/* Ricette collaudate (se esistono per la categoria) */}
      {recipes.length > 0 && (
        <>
          <h5 className="font-semibold text-sm mb-2" style={{ color: COLORS.primary }}>
            {catId === 'amaro' ? 'Scegli il tuo Amaro' : catId === 'gin' ? 'Scegli la variante di Gin' : 'Ricetta collaudata'}
          </h5>
          <div className="space-y-3 mb-3">
            {recipes.map((r) => (
              <RecipeCard
                key={r.id}
                recipe={r}
                selected={product.ricettaScelta === r.id && product.recipeMode === 'standard'}
                onClick={() => updateProduct({ ricettaScelta: r.id, recipeMode: 'standard' })}
              />
            ))}
          </div>
        </>
      )}

      {/* Se categoria ha 1 sola "ricetta default" non in array */}
      {recipes.length === 0 && catId !== 'altro' && (
        <>
          <h5 className="font-semibold text-sm mb-2" style={{ color: COLORS.primary }}>Ricetta collaudata</h5>
          <RecipeCard
            recipe={{
              id: `default-${catId}`,
              icon: cat?.icon,
              name: `Ricetta collaudata ${cat?.name}`,
              tags: ['Artigianale', 'Pluripremiato'],
              desc: 'La nostra ricetta di riferimento, sviluppata e perfezionata nel tempo. Campioni disponibili prima della produzione.',
              tasting: 'Il team ti invierà scheda tecnica completa e campione fisico dopo la richiesta di preventivo.',
            }}
            selected={product.ricettaScelta === `default-${catId}` && product.recipeMode === 'standard'}
            onClick={() => updateProduct({ ricettaScelta: `default-${catId}`, recipeMode: 'standard' })}
          />
        </>
      )}

      {/* Separator + Ricetta personalizzata — nascosti per categorie senza personalizzazione */}
      {allowCustom && (
      <>
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 font-medium">— oppure —</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Custom card */}
      <button
        type="button"
        onClick={() => updateProduct({ recipeMode: 'custom', ricettaScelta: 'personalizzata' })}
        data-testid={`recipe-custom-${catId}`}
        className={`w-full text-left p-4 rounded-lg border-2 border-dashed transition-all ${product.recipeMode === 'custom' ? 'shadow-md' : 'hover:border-gray-400'}`}
        style={{
          borderColor: product.recipeMode === 'custom' ? COLORS.primary : '#D1D5DB',
          backgroundColor: product.recipeMode === 'custom' ? COLORS.primarySoft : '#FAFAFA',
        }}
      >
        <div className="font-semibold mb-1">🌿 Voglio una ricetta personalizzata per questo liquore</div>
        <div className="text-xs text-gray-500 mb-2">Seleziona le botaniche e costruisci il profilo aromatico con il nostro mastro liquorista.</div>
        <div className="inline-block text-xs px-2 py-1 rounded font-medium" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
          ⚗️ Ordine minimo 120 bottiglie | Tempi di sviluppo aggiuntivi
        </div>
      </button>
      </>
      )}

      {/* Custom config panel */}
      {product.recipeMode === 'custom' && (
        <div className="mt-4 animate-fadeIn">
          <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: COLORS.gold, color: COLORS.primary }}>
            <div className="font-bold mb-1">⚗️ Ricetta personalizzata — ordine minimo 120 bottiglie</div>
          </div>

          <h6 className="font-semibold text-sm mb-1" style={{ color: COLORS.primary }}>Seleziona le tue botaniche e scegli l'intensità</h6>
          <div className="text-sm font-medium mb-2" style={{ color: COLORS.primary }}>
            {product.botaniche.length} botaniche selezionate
            {product.botaniche.length < 3 && (
              <span className="ml-2 text-gray-500 font-normal">— minimo 3</span>
            )}
          </div>

          {Object.entries(BOTANICALS).map(([category, items]) => (
            <div key={category} className="mb-3">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">{category}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {items.map((name) => {
                  const sel = product.botaniche.find((b) => b.name === name);
                  return (
                    <div
                      key={name}
                      className={`p-2.5 rounded-lg border-2 cursor-pointer transition-all ${sel ? 'shadow-sm' : 'hover:border-gray-400'}`}
                      style={{
                        borderColor: sel ? COLORS.primary : '#E5E7EB',
                        backgroundColor: sel ? COLORS.primarySoft : 'white',
                      }}
                      data-testid={`botanica-${catId}-${name}`}
                      onClick={() => toggleBotanica(name, category)}
                    >
                      <div className="text-xs font-medium">{name}</div>
                      {sel && (
                        <div className="mt-1.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          {[1, 2, 3, 4, 5].map((i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setIntensity(name, i)}
                              className="w-4 h-4 rounded-full"
                              style={{
                                backgroundColor: i <= sel.intensity ? COLORS.accent : 'transparent',
                                border: `1.5px solid ${i <= sel.intensity ? COLORS.accent : '#D1D5DB'}`,
                              }}
                              title={INTENSITY_LABELS[i - 1]}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {errors[`botaniche-${catId}`] && <p className="text-sm" style={{ color: COLORS.accent }}>Servono almeno 3 botaniche.</p>}

          {/* Gradazione */}
          <h6 className="font-semibold text-sm mt-4 mb-2" style={{ color: COLORS.primary }}>Gradazione alcolica</h6>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
            {ALCOHOL_RANGES.map((r) => (
              <SelectableCard
                key={r.id}
                selected={product.gradazione.range === r.id}
                onClick={() => updateProduct({ gradazione: { ...product.gradazione, range: r.id } })}
                testid={`gradazione-${catId}-${r.id}`}
              >
                <div className="text-xl mb-0.5">{r.icon}</div>
                <div className="font-semibold text-xs">{r.title}</div>
                <div className="text-xs font-medium" style={{ color: COLORS.accent }}>{r.range}</div>
              </SelectableCard>
            ))}
          </div>
          {errors[`gradazione-${catId}`] && <p className="text-sm" style={{ color: COLORS.accent }}>Seleziona una gradazione.</p>}

          <div className="mt-3">
            <label className="block text-xs font-medium mb-1">Gradazione esatta (opzionale)</label>
            <input
              type="number"
              min="15"
              max="60"
              value={product.gradazione.precise}
              onChange={(e) => updateProduct({ gradazione: { ...product.gradazione, precise: e.target.value } })}
              className={inputBase(false)}
              placeholder="Es: 32"
              data-testid={`input-gradazione-precisa-${catId}`}
            />
          </div>

          <div className="mt-3">
            <label className="block text-xs font-medium mb-1">Note sul profilo (opzionale)</label>
            <textarea
              rows={2}
              value={product.noteRicetta}
              onChange={(e) => updateProduct({ noteRicetta: e.target.value })}
              className={inputBase(false)}
              placeholder="Es: amaro secco con note agrumate e finale speziato..."
              data-testid={`input-note-ricetta-${catId}`}
            />
          </div>
        </div>
      )}

      {errors[`recipeMode-${catId}`] && (
        <p className="mt-2 text-sm" style={{ color: COLORS.accent }}>Scegli ricetta collaudata o personalizzata.</p>
      )}
      {errors[`ricetta-${catId}`] && (
        <p className="mt-2 text-sm" style={{ color: COLORS.accent }}>Seleziona una ricetta.</p>
      )}
    </div>
  );
}

function RecipeCard({ recipe, selected, onClick }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`rounded-lg border-2 transition-all ${selected ? 'shadow-md' : 'hover:border-gray-400'}`}
      style={{
        borderColor: selected ? COLORS.primary : '#E5E7EB',
        backgroundColor: selected ? COLORS.primarySoft : 'white',
      }}
    >
      <button
        type="button"
        onClick={onClick}
        data-testid={`recipe-${recipe.id}`}
        className="w-full text-left p-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          <div className="text-3xl">{recipe.icon}</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold mb-1.5" style={{ color: COLORS.primary }}>{recipe.name}</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {recipe.tags.map((t) => (
                <span key={t} className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: COLORS.primarySoft, color: COLORS.primary }}>{t}</span>
              ))}
            </div>
            <div className="text-sm text-gray-600 leading-snug">{recipe.desc}</div>
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="w-full text-xs text-left px-4 sm:px-5 pb-3 hover:underline"
        style={{ color: COLORS.accent }}
      >
        {open ? '▲ Nascondi note di degustazione' : '▼ Mostra note di degustazione'}
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-4 text-sm text-gray-700 italic border-t pt-3" style={{ borderColor: '#E5E7EB' }}>
          {recipe.tasting}
        </div>
      )}
    </div>
  );
}

// ---- STEP 5 ----
function Step5({ state, hasCustom, totalQuantity, suggestedFascia, toggleProductFormat, setProductFormatQty, pricingData, errors }) {
  const { products = [], formats: dbFormats = [], tiers = [] } = pricingData || {};
  const sortedTiers = useMemo(() => [...tiers].sort((a, b) => (a.min_quantity || 0) - (b.min_quantity || 0)), [tiers]);

  const parseCl = (label) => {
    const m = label?.match(/(\d+)\s*cl/i);
    return m ? parseInt(m[1], 10) : null;
  };

  const getDbProduct = (catId) => {
    // 1) Match esplicito tramite linked_product_id configurato in admin (priorità massima)
    const linkedId = CATEGORY_PRODUCT_LINKS[catId];
    if (linkedId) {
      const linked = products.find((dp) => dp.id?.toLowerCase() === String(linkedId).toLowerCase());
      if (linked) return linked;
    }
    // 2) Fallback: match per id slug = categoryId
    return products.find((dp) => dp.id?.toLowerCase() === catId.toLowerCase());
  };
  const getDbFormat = (fmtLabel) => dbFormats.find((f) => f.size_cl === parseCl(fmtLabel));

  const calcUnit = (basePrice, multiplier, discount) => basePrice * multiplier * (1 - (discount || 0));

  return (
    <>
      <StepHeader
        title="Che formati e quantità ti servono?"
        subtitle="Per ogni liquore seleziona i formati e clicca lo scaglione di quantità che ti interessa."
      />

      {hasCustom && (
        <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
          ℹ️ Hai selezionato almeno una ricetta personalizzata — la quantità minima totale è 120 bottiglie.
        </div>
      )}

      <div className="space-y-4">
        {state.prodotti.map((p) => {
          const cat = CATEGORIES.find((c) => c.id === p.categoryId);
          const catName = cat?.name || p.categoryId;
          const recipe = getRecipesForCategory(p.categoryId).find((r) => r.id === p.ricettaScelta);
          const productSubtotal = (p.formatItems || []).reduce((s, fi) => s + (parseInt(fi.quantity, 10) || 0), 0);
          const productLabel = p.categoryId === 'altro' && p.customName
            ? `${catName} — ${p.customName}`
            : recipe ? `${catName} — ${recipe.name}` : catName;
          const hasMissing = productSubtotal === 0;
          const dbProduct = getDbProduct(p.categoryId);
          const pricingAvailable = !!(dbProduct && sortedTiers.length > 0 && p.recipeMode === 'standard' && p.categoryId !== 'altro');

          return (
            <div
              key={p.categoryId}
              className="rounded-xl border-2 p-4 sm:p-5 transition-all"
              style={{
                borderColor: hasMissing ? COLORS.accent : COLORS.primarySoft,
                backgroundColor: hasMissing ? '#FDF6FB' : '#FCFBFD',
              }}
              data-testid={`step5-product-${p.categoryId}`}
            >
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-2xl">{cat?.icon}</span>
                <h4 className="font-display text-lg" style={{ color: COLORS.primary }}>{productLabel}</h4>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                  {getProductAlcohol(p).toFixed(0)}% vol.
                </span>
                <span
                  className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: hasMissing ? COLORS.accent : '#DCFCE7',
                    color: hasMissing ? 'white' : '#166534',
                  }}
                >
                  {productSubtotal === 0 ? 'Da configurare' : `${productSubtotal} bot.`}
                </span>
              </div>

              {/* Selettore formati (chips) */}
              <div className="flex flex-wrap gap-2 mb-3">
                {BOTTLE_FORMATS.map((fmt) => {
                  const item = (p.formatItems || []).find((fi) => fi.format === fmt);
                  const selected = !!item;
                  return (
                    <button
                      key={fmt}
                      type="button"
                      onClick={() => toggleProductFormat(p.categoryId, fmt)}
                      data-testid={`step5-fmt-${p.categoryId}-${fmt.replace(/\s/g, '-')}`}
                      className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-2 ${selected ? 'shadow-sm' : 'hover:border-gray-400'}`}
                      style={{
                        borderColor: selected ? COLORS.primary : '#E5E7EB',
                        backgroundColor: selected ? COLORS.primarySoft : 'white',
                      }}
                    >
                      <EmptyBottleIcon color={selected ? COLORS.accent : '#6B7280'} />
                      <span>{fmt}</span>
                      {selected && <span style={{ color: COLORS.accent }}>✓</span>}
                    </button>
                  );
                })}
              </div>

              {/* Per ogni formato selezionato, mostra tabella scaglioni + input quantità */}
              {(p.formatItems || []).map((fi) => {
                const dbFmt = getDbFormat(fi.format);
                const qty = parseInt(fi.quantity, 10) || 0;
                const currentTier = sortedTiers.find((t) => qty >= (t.min_quantity || 0) && (t.max_quantity == null || qty <= t.max_quantity));
                return (
                  <div key={fi.format} className="mt-3 p-3 rounded-lg border" style={{ borderColor: COLORS.primarySoft, backgroundColor: 'white' }}>
                    <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                      <span className="text-sm font-semibold" style={{ color: COLORS.primary }}>
                        Formato {fi.format}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleProductFormat(p.categoryId, fi.format)}
                        className="text-xs text-gray-400 hover:text-red-500"
                        title="Rimuovi formato"
                        data-testid={`step5-remove-${p.categoryId}-${fi.format.replace(/\s/g, '-')}`}
                      >
                        ✕ rimuovi
                      </button>
                    </div>

                    {pricingAvailable && dbFmt ? (
                      <>
                        <p className="text-[11px] text-gray-500 mb-2">Clicca lo scaglione di quantità che vuoi richiedere — il prezzo unitario è calcolato sul nostro listino ufficiale.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid={`step5-tiers-${p.categoryId}-${fi.format.replace(/\s/g, '-')}`}>
                          {sortedTiers.map((tier) => {
                            const unit = calcUnit(dbProduct.basePrice, dbFmt.price_multiplier, tier.discount_percentage);
                            const active = currentTier?.id === tier.id;
                            return (
                              <button
                                key={tier.id}
                                type="button"
                                onClick={() => setProductFormatQty(p.categoryId, fi.format, tier.min_quantity)}
                                data-testid={`step5-tier-${p.categoryId}-${fi.format.replace(/\s/g, '-')}-${tier.min_quantity}`}
                                className={`p-2 rounded-lg border-2 transition-all text-left ${active ? 'shadow-md' : 'hover:border-gray-400'}`}
                                style={{
                                  borderColor: active ? COLORS.accent : '#E5E7EB',
                                  backgroundColor: active ? '#FDF2F8' : 'white',
                                }}
                              >
                                <div className="text-[10px] font-semibold text-gray-600">{tier.label}</div>
                                <div className="text-base font-bold font-mono" style={{ color: active ? COLORS.accent : COLORS.text }}>
                                  €{unit.toFixed(2)}
                                </div>
                                {tier.discount_percentage > 0 && (
                                  <div className="text-[10px] font-bold" style={{ color: COLORS.accent }}>
                                    -{Math.round(tier.discount_percentage * 100)}%
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <p className="text-xs italic text-gray-500 mb-2">
                        💬 Prezzo da definire dal nostro team (ricetta personalizzata / prodotto su misura).
                      </p>
                    )}

                    {/* Input quantità precisa */}
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <label className="text-xs font-medium text-gray-700">Quantità precisa:</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={fi.quantity || ''}
                        placeholder="0"
                        onChange={(e) => setProductFormatQty(p.categoryId, fi.format, e.target.value)}
                        data-testid={`step5-qty-${p.categoryId}-${fi.format.replace(/\s/g, '-')}`}
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <span className="text-xs text-gray-500">bottiglie</span>
                      {pricingAvailable && dbFmt && qty > 0 && currentTier && (
                        <span className="ml-auto text-sm font-bold font-mono" style={{ color: COLORS.primary }}>
                          = €{(calcUnit(dbProduct.basePrice, dbFmt.price_multiplier, currentTier.discount_percentage) * qty).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="mt-5 rounded-lg p-4 flex items-center justify-between flex-wrap gap-3" style={{ backgroundColor: COLORS.primarySoft }} data-testid="step5-totals">
        <div>
          <div className="text-xs text-gray-600">Totale complessivo</div>
          <div className="font-display text-2xl" style={{ color: COLORS.primary }}>
            {totalQuantity} {totalQuantity === 1 ? 'bottiglia' : 'bottiglie'}
          </div>
        </div>
        {suggestedFascia && (
          <div className="text-right">
            <div className="text-xs text-gray-600">Fascia di prezzo</div>
            <div className="font-semibold text-sm" style={{ color: COLORS.accent }}>
              {suggestedFascia === 'starter' && '🥉 Starter'}
              {suggestedFascia === 'business' && '🥈 Business'}
              {suggestedFascia === 'volume' && '🥇 Volume'}
            </div>
          </div>
        )}
      </div>

      {errors.minQty && <p className="mt-2 text-sm" style={{ color: COLORS.accent }}>Ordine minimo 24 bottiglie totali.</p>}
    </>
  );
}

// ---- STEP 6 ----
function Step6({ state, update, errors }) {
  return (
    <>
      <StepHeader title="Diamo un volto al tuo prodotto" subtitle="Il nostro team grafico interno ti seguirà in ogni fase — dall'idea alla bottiglia finita." />

      <div className="space-y-3">
        <SelectableCard selected={state.logoMode === 'upload'} onClick={() => update({ logoMode: 'upload' })} testid="logo-upload" full>
          <div className="font-semibold">📁 Carico il mio logo ora</div>
          {state.logoMode === 'upload' && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              <input
                type="file"
                accept="image/png,image/svg+xml,application/pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 5 * 1024 * 1024) { alert('File troppo grande (max 5 MB)'); return; }
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    update({
                      logoFileName: f.name,
                      logoFileData: ev.target.result, // data URL completo
                      logoFileMime: f.type || 'application/octet-stream',
                    });
                  };
                  reader.onerror = () => alert('Errore lettura file');
                  reader.readAsDataURL(f);
                }}
                className="text-sm"
                data-testid="input-logo-file"
              />
              {state.logoFileName && <p className="text-xs mt-1" style={{ color: COLORS.primary }}>✅ {state.logoFileName}</p>}
            </div>
          )}
        </SelectableCard>
        <SelectableCard selected={state.logoMode === 'email'} onClick={() => update({ logoMode: 'email' })} testid="logo-email" full>
          <div className="font-semibold">📧 Invierò il logo via email dopo</div>
          {state.logoMode === 'email' && (
            <p className="text-xs mt-2" style={{ color: COLORS.accent }}>Perfetto — ricordati di inviarlo a preventivi@iltuoamaro.it</p>
          )}
        </SelectableCard>
        <SelectableCard selected={state.logoMode === 'support'} onClick={() => update({ logoMode: 'support' })} testid="logo-support" full>
          <div className="font-semibold">🎨 Ho bisogno di supporto per creare il brand da zero</div>
          {state.logoMode === 'support' && (
            <label className="flex items-center gap-2 mt-3 text-sm" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={state.brandingService}
                onChange={(e) => update({ brandingService: e.target.checked })}
                data-testid="checkbox-branding-service"
              />
              ✅ Includi servizio di branding professionale nel preventivo
            </label>
          )}
        </SelectableCard>
      </div>
      {errors.logoMode && <p className="mt-3 text-sm" style={{ color: COLORS.accent }}>Seleziona un'opzione.</p>}

      <div className="mt-5">
        <label className="block text-sm font-medium mb-1.5">Note per il grafico (opzionale)</label>
        <textarea
          rows={3}
          value={state.noteGrafico}
          onChange={(e) => update({ noteGrafico: e.target.value })}
          className={inputBase(false)}
          placeholder="Es: colori preferiti, stile, nome del prodotto, riferimenti visivi che ti ispirano..."
          data-testid="input-note-grafico"
        />
      </div>
    </>
  );
}

// ---- STEP 7 ----
function Step7({ state, update, submitting, onGenerate, goBack, pricingData }) {
  const settoriLabels = state.settori.map((s) => s === 'altro' ? `Altro: ${state.settoreAltro}` : findLabel(SECTORS, s)).join(', ');
  const destLabel = state.destinazione === 'altro' ? `Altro: ${state.destinazioneAltro}` : findLabel(DESTINATIONS, state.destinazione, 'title');
  const logoLabel = ({
    upload: state.logoFileName ? `Caricato: ${state.logoFileName}` : 'In caricamento',
    email: 'Da inviare via email',
    support: state.brandingService ? 'Branding professionale richiesto' : 'Supporto richiesto',
  })[state.logoMode] || '—';

  const prodottiLabels = state.prodotti.map((p) => {
    const cat = CATEGORIES.find((c) => c.id === p.categoryId);
    const catName = cat?.name || p.categoryId;
    const recipe = getRecipesForCategory(p.categoryId).find((r) => r.id === p.ricettaScelta);
    const mode = p.recipeMode === 'custom' ? 'Personalizzata' : (recipe?.name || 'Collaudata');
    const name = p.categoryId === 'altro' && p.customName ? `${catName} (${p.customName})` : catName;
    const fmtSummary = (p.formatItems || []).map((fi) => `${fi.format} ×${fi.quantity}`).join(' + ');
    const grado = `${getProductAlcohol(p).toFixed(0)}% vol.`;
    return `${name} (${grado}) — ${mode}${fmtSummary ? ` [${fmtSummary}]` : ''}`;
  });

  const totalQty = state.prodotti.reduce(
    (sum, p) => sum + (p.formatItems || []).reduce((s, fi) => s + (parseInt(fi.quantity, 10) || 0), 0),
    0
  );

  return (
    <>
      <StepHeader title="Perfetto! Manda la tua richiesta." subtitle="Controlla il riepilogo e i prezzi indicativi. Il nostro team ti contatterà con un'offerta su misura entro 24 ore lavorative." />

      <div className="rounded-xl border-2 p-5" style={{ borderColor: COLORS.primarySoft, backgroundColor: '#FCFBFD' }} data-testid="lead-summary">
        <Row label="Cliente" value={state.tipoCliente === 'azienda' ? state.ragioneSociale : `${state.nome} ${state.cognome}`.trim()} />
        <Row label="Contatti" value={`${state.email} • ${state.telefono}`} />
        <Row label="Settore" value={settoriLabels} />
        <Row label="Destinazione" value={destLabel} />
        <Row label="Liquori" value={prodottiLabels.join(' | ')} />
        <Row label="Totale bottiglie" value={totalQty > 0 ? `${totalQty} bottiglie` : '—'} />
        <Row label="Fascia" value={state.fascia ? state.fascia.charAt(0).toUpperCase() + state.fascia.slice(1) : '—'} />
        <Row label="Logo" value={logoLabel} />
      </div>

      {/* Tabella prezzi indicativi (stesso motore di /listino) */}
      <PricingTable state={state} pricingData={pricingData} />

      <div className="mt-5">
        <label className="block text-sm font-medium mb-1.5">C'è qualcos'altro che vuoi dirci? (opzionale)</label>
        <textarea
          rows={3}
          value={state.noteFinali}
          onChange={(e) => update({ noteFinali: e.target.value })}
          className={inputBase(false)}
          placeholder="Tempistiche, esigenze particolari, domande..."
          data-testid="input-note-finali"
        />
      </div>

      <button
        type="button"
        onClick={onGenerate}
        disabled={submitting}
        data-testid="lead-generate-pdf-btn"
        className={`w-full mt-6 px-8 py-4 rounded-lg font-bold text-white text-lg shadow-lg disabled:opacity-60 ${!submitting ? 'pulse-cta' : ''}`}
        style={{ backgroundColor: COLORS.primary }}
      >
        {submitting ? '⏳ Stiamo registrando la tua richiesta...' : '🚀 Invia la mia richiesta'}
      </button>

      <div className="flex justify-start mt-3">
        <button
          type="button"
          onClick={goBack}
          disabled={submitting}
          className="text-sm font-medium text-gray-600 hover:text-gray-900 px-4 py-2 disabled:opacity-50"
          data-testid="lead-back-btn"
        >
          ← Indietro (modifica)
        </button>
      </div>

      <p className="text-xs text-center text-gray-500 mt-3">
        Cliccando confermi di voler ricevere un'offerta personalizzata dal nostro team entro 24 ore lavorative.
      </p>
    </>
  );
}

// ---- PRICING TABLE (stesso motore di /listino) ----
function PricingTable({ state, pricingData }) {
  const { products = [], formats = [], tiers = [] } = pricingData || {};
  if (!products.length || !formats.length || !tiers.length) return null;

  const parseCl = (label) => {
    const m = label?.match(/(\d+)\s*cl/i);
    return m ? parseInt(m[1], 10) : null;
  };

  const findTierForQty = (qty) => {
    const sortedTiers = [...tiers].sort((a, b) => (a.min_quantity || 0) - (b.min_quantity || 0));
    return sortedTiers.find((t) => qty >= (t.min_quantity || 0) && (t.max_quantity == null || qty <= t.max_quantity));
  };

  // Per ogni prodotto standard mostriamo una mini-tabella con i formatItems del prodotto
  const matchedProducts = state.prodotti
    .filter((p) => p.categoryId !== 'altro' && p.recipeMode === 'standard' && (p.formatItems || []).length > 0)
    .map((p) => {
      const cat = CATEGORIES.find((c) => c.id === p.categoryId);
      const db = products.find((dp) =>
        dp.id?.toLowerCase() === p.categoryId.toLowerCase()
        || dp.name?.toLowerCase() === (cat?.name || '').toLowerCase()
      );
      return db ? { product: p, cat, db } : null;
    })
    .filter(Boolean);

  if (matchedProducts.length === 0) {
    return (
      <div className="mt-6 rounded-xl border-2 border-dashed p-4 text-center" style={{ borderColor: COLORS.primarySoft, backgroundColor: '#FCFBFD' }}>
        <p className="text-sm text-gray-600">
          💬 I prezzi definitivi (incluse ricette personalizzate / formati custom / branding professionale) ti verranno comunicati dal nostro team entro 24 ore lavorative.
        </p>
      </div>
    );
  }

  // Totale ordine
  const grandTotal = matchedProducts.reduce((tot, mp) => {
    return tot + mp.product.formatItems.reduce((s, fi) => {
      const cl = parseCl(fi.format);
      const dbFmt = formats.find((f) => f.size_cl === cl);
      if (!dbFmt) return s;
      const tier = findTierForQty(fi.quantity);
      const discount = tier?.discount_percentage || 0;
      const unitPrice = mp.db.basePrice * dbFmt.price_multiplier * (1 - discount);
      return s + unitPrice * fi.quantity;
    }, 0);
  }, 0);

  return (
    <div className="mt-6" data-testid="lead-pricing-table">
      <h3 className="font-display text-xl mb-1" style={{ color: COLORS.primary }}>💰 Prezzi indicativi</h3>
      <p className="text-xs text-gray-500 mb-3">
        Stima IVA esclusa basata sul nostro listino ufficiale. I prezzi finali saranno confermati dal team in base alla ricetta, branding e volumi reali.
      </p>

      {matchedProducts.map(({ product, cat, db }, idx) => {
        const productTotal = product.formatItems.reduce((s, fi) => s + (parseInt(fi.quantity, 10) || 0), 0);
        return (
          <div key={`${product.categoryId}-${idx}`} className="mb-4 rounded-xl overflow-hidden border" style={{ borderColor: COLORS.primarySoft }} data-testid={`pricing-product-${product.categoryId}`}>
            <div className="px-4 py-2 flex items-center gap-2" style={{ backgroundColor: COLORS.primary, color: 'white' }}>
              <span className="text-xl">{cat?.icon}</span>
              <span className="font-semibold">{cat?.name}</span>
              <span className="ml-auto text-xs opacity-80">{productTotal} bottiglie • Base €{db.basePrice?.toFixed(2)} / 50cl</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="bg-purple-50 text-purple-900">
                    <th className="px-2 py-2 text-left font-semibold">Formato</th>
                    <th className="px-2 py-2 text-right font-semibold">Quantità</th>
                    <th className="px-2 py-2 text-right font-semibold">Fascia</th>
                    <th className="px-2 py-2 text-right font-semibold">Prezzo unitario</th>
                    <th className="px-2 py-2 text-right font-semibold">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {product.formatItems.map((fi) => {
                    const cl = parseCl(fi.format);
                    const dbFmt = formats.find((f) => f.size_cl === cl);
                    if (!dbFmt) {
                      return (
                        <tr key={fi.format} className="border-t border-purple-100">
                          <td className="px-2 py-2 font-medium">{fi.format}</td>
                          <td className="px-2 py-2 text-right">{fi.quantity}</td>
                          <td className="px-2 py-2 text-right text-gray-400 italic" colSpan={3}>Da definire</td>
                        </tr>
                      );
                    }
                    const tier = findTierForQty(fi.quantity);
                    const discount = tier?.discount_percentage || 0;
                    const unitPrice = db.basePrice * dbFmt.price_multiplier * (1 - discount);
                    const lineTotal = unitPrice * fi.quantity;
                    return (
                      <tr key={fi.format} className="border-t border-purple-100">
                        <td className="px-2 py-2 font-medium text-gray-700">{fi.format}</td>
                        <td className="px-2 py-2 text-right font-mono">{fi.quantity}</td>
                        <td className="px-2 py-2 text-right text-xs text-gray-600">
                          {tier?.label || '—'}
                          {discount > 0 && <span className="ml-1 text-pink-600 font-bold">-{Math.round(discount * 100)}%</span>}
                        </td>
                        <td className="px-2 py-2 text-right font-mono text-gray-800">€{unitPrice.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right font-mono font-semibold" style={{ color: COLORS.primary }}>€{lineTotal.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Grand total */}
      <div className="mt-4 rounded-lg p-4 flex items-center justify-between" style={{ backgroundColor: COLORS.primary, color: 'white' }} data-testid="pricing-grand-total">
        <div>
          <div className="text-xs opacity-80">Totale ordine indicativo (IVA esclusa)</div>
          <div className="text-[10px] opacity-70 mt-0.5">Spedizione gratuita inclusa per ordini idonei</div>
        </div>
        <div className="font-display text-2xl font-bold">€{grandTotal.toFixed(2)}</div>
      </div>

      <p className="text-[11px] text-gray-400 italic mt-2">
        ℹ️ Prezzi indicativi su listino ufficiale. Sconti applicati per ogni formato in base alla sua quantità. I prezzi definitivi saranno confermati dal nostro team commerciale.
      </p>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 py-2 border-b last:border-b-0" style={{ borderColor: COLORS.primarySoft }}>
      <div className="text-xs sm:text-sm font-bold sm:w-32" style={{ color: COLORS.primary }}>{label}</div>
      <div className="text-sm flex-1 break-words">{value}</div>
    </div>
  );
}
